import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import type { PortalCampoConfig, PortalAparencia, PortalFuncionalidades, PortalMensagem } from '../../types';
import { compressImagesIfNeeded } from '../../utils/imageCompression';
import PortalCotacao from '../Cotacao/PortalCotacao';
// Mesmos componentes do formulário interno de card — os endpoints que eles usam
// (/aeroportos/buscar, /voos/estimar-preco, /rota-terrestre/*) são públicos.
import { RotasField } from '../common/AeroportoSearch';
import { RotaTerrestreField } from '../common/RotaTerrestreField';

// API base - usa URL relativa (funciona com Cloudflare tunnel, IP direto, etc.)
const API_BASE = (import.meta as any).env?.VITE_API_URL || '';

// ============================================================
// TIPOS E INTERFACES
// ============================================================

interface AutomacaoExibicao {
  id: string;
  nome: string;
  acoes: Array<{
    tipo: string;
    configuracao?: {
      regras?: Array<{
        condicao: {
          campoId: string;
          operador: string;
          valor?: string | number;
        };
        acao?: string;
        comportamento?: string;
        elementosAfetados?: Array<{ tipo: string; id: string; nome: string }>;
        acaoEntao?: { acao: string; campo: string };
        acaoSenao?: { acao: string; campo: string };
      }>;
    };
  }>;
}

interface PortalPublicoData {
  id: string;
  nome: string;
  descricao?: string;
  campos: PortalCampoConfig[];
  camposParaExibir?: Array<{ id: string; nome: string; tipo: string; valor: any }>;
  aparencia: PortalAparencia;
  funcionalidades: PortalFuncionalidades;
  textos: {
    mensagemInicial?: string;
    mensagemSucesso?: string;
    botaoEnviar?: string;
    tituloPaginaAcompanhamento?: string;
  };
  flowNome?: string;
  etapaDestino?: { id: string; nome: string };
  automacoes?: AutomacaoExibicao[];
  documentos?: Array<{
    id: string;
    nome: string;
    descricao?: string;
    conteudoHtml?: string;
    tipo?: string;
    pdfUrl?: string;
  }>;
}

// 📎 Pedido de reenvio de anexo feito pela equipe interna
interface SolicitacaoReenvioCampo {
  campoId: string;
  nome: string;
  motivo?: string;
  tipo?: string;
  minAnexos?: number;
  maxAnexos?: number;
  anexosAtuais?: any[];
}

interface SolicitacaoReenvio {
  id: string;
  status: 'pendente' | 'atendida';
  campos: SolicitacaoReenvioCampo[];
  observacao?: string;
  solicitadoEm?: string;
  solicitadoPorNome?: string;
}

interface SubmissaoData {
  id: string;
  protocolo?: string;
  status: string;
  dados: Record<string, any>;
  dadosFormatados?: Record<string, any>;
  nomeExterno?: string;
  emailExterno?: string;
  createdAt: string;
  updatedAt: string;
  cardCriadoId?: string;
  etapaAtual?: { id: string; nome: string };
  mensagens: PortalMensagem[];
  totalMensagens: number;
  mensagensNaoLidas: number;
  tokenAcesso?: string;
  solicitacaoReenvio?: SolicitacaoReenvio | null;
  aprovacaoSolicitante?: AprovacaoSolicitante | null;
  reprovacao?: Reprovacao | null;
}

// Card em etapa de reprovação: o servidor manda só o rótulo de "Motivos internos",
// nunca o texto interno da analista (08/10/2026).
interface Reprovacao {
  etapaNome: string;
  motivos: { nome: string; detalhe?: string; interno?: boolean }[];
  soMotivoInterno?: boolean;
}

// Etapa com etapa.aprovacaoPeloSolicitante: quem abriu o pedido aprova (ou não) a
// entrega pelo próprio acompanhamento, e o card anda sozinho (08/10/2026).
interface AprovacaoSolicitante {
  etapaId: string;
  etapaNome: string;
  pergunta: string;
  entregas: { campoId: string; nome: string; anexos: { nome: string; url: string; type?: string }[] }[];
  textos: { nome: string; valor: string }[];
}

interface MinhaSubmissao {
  id: string;
  protocolo?: string;
  status: string;
  etapaNome: string;
  nomeExterno?: string;
  emailExterno?: string;
  createdAt: string;
  updatedAt?: string;
  ultimaInteracao?: string;
  mensagensNaoLidas: number;
  totalMensagens: number;
  tokenAcesso?: string;
}

interface SubmissaoResposta {
  success: boolean;
  message?: string;
  submissaoId?: string;
  protocolo?: string;
  tokenAcesso?: string;
  urlAcompanhamento?: string;
  error?: string;
}

type TelaAtual = 'inicio' | 'login' | 'dashboard' | 'formulario' | 'detalhes' | 'sucesso';

// ============================================================
// INPUT COM STATE LOCAL (evita re-render do pai a cada tecla)
// ============================================================

const DebouncedInput = memo(({ value: externalValue, onChange, type = 'text', placeholder, className, style, rows, inputMode }: {
  value: string;
  onChange: (val: string) => void;
  type?: string;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  rows?: number;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}) => {
  const [localValue, setLocalValue] = useState(externalValue || '');
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const isFocusedRef = useRef(false);

  // Sync from parent only when NOT focused (e.g. clear button, initial load)
  useEffect(() => {
    if (!isFocusedRef.current) {
      setLocalValue(externalValue || '');
    }
  }, [externalValue]);

  const handleLocalChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const val = e.target.value;
    setLocalValue(val);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onChangeRef.current(val), 300);
  }, []);

  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
    clearTimeout(timerRef.current);
    onChangeRef.current(localValue);
  }, [localValue]);

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);

  if (rows) {
    return <textarea value={localValue} onChange={handleLocalChange} onBlur={handleBlur} onFocus={handleFocus} placeholder={placeholder} rows={rows} className={className} style={style} />;
  }
  return <input type={type} value={localValue} onChange={handleLocalChange} onBlur={handleBlur} onFocus={handleFocus} placeholder={placeholder} className={className} style={style} inputMode={inputMode} />;
}, (prev, next) => {
  // Custom comparison: ignore onChange and style (new refs every render)
  return prev.value === next.value && prev.type === next.type && prev.placeholder === next.placeholder && prev.className === next.className && prev.rows === next.rows;
});

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================

const PortalPublico = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // IDs do modo acompanhamento
  const submissaoIdParam = searchParams.get('submissaoId');
  const tokenAcessoParam = searchParams.get('t');
  
  // Estados principais
  const [portal, setPortal] = useState<PortalPublicoData | null>(null);
  const [dadosCotacao, setDadosCotacao] = useState<any | null>(null); // 💰 link de cotação
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [telaAtual, setTelaAtual] = useState<TelaAtual>('inicio');
  
  // Estados de autenticação do terceiro
  const [emailLogin, setEmailLogin] = useState('');
  const [protocoloLogin, setProtocoloLogin] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [usuarioEmail, setUsuarioEmail] = useState<string | null>(null);

  // 🔐 Acesso por código (portal.funcionalidades.acessoPorCodigo): o e-mail é
  // provado por um código enviado para ele. A sessão fica só NESTA ABA
  // (sessionStorage) e vai no header X-Portal-Sessao — nada de acesso na URL.
  const sessaoStorageKey = `portal_sessao_${token || ''}`;
  const lerSessaoGuardada = (): { sessao: string; email: string; expiraEm: string } | null => {
    try {
      const raw = sessionStorage.getItem(sessaoStorageKey);
      if (!raw) return null;
      const guardada = JSON.parse(raw);
      if (!guardada?.sessao || !guardada?.expiraEm || new Date(guardada.expiraEm).getTime() <= Date.now()) {
        sessionStorage.removeItem(sessaoStorageKey);
        return null;
      }
      return guardada;
    } catch {
      return null;
    }
  };
  const [sessaoPortal, setSessaoPortal] = useState(lerSessaoGuardada);
  const sessaoRef = useRef(sessaoPortal);
  sessaoRef.current = sessaoPortal;
  const [etapaCodigo, setEtapaCodigo] = useState<'email' | 'codigo'>('email');
  const [codigoLogin, setCodigoLogin] = useState('');
  const [avisoLogin, setAvisoLogin] = useState<string | null>(null);
  // Solicitação que a pessoa tentou abrir (link do e-mail) antes de ter sessão
  const submissaoPendenteRef = useRef<string | null>(null);
  const exigeCodigo = !!portal?.funcionalidades?.acessoPorCodigo;
  const headersPortal = (json = true): Record<string, string> => {
    const h: Record<string, string> = {};
    if (json) h['Content-Type'] = 'application/json';
    if (sessaoRef.current?.sessao) h['X-Portal-Sessao'] = sessaoRef.current.sessao;
    return h;
  };
  const encerrarSessaoLocal = () => {
    try { sessionStorage.removeItem(sessaoStorageKey); } catch {}
    sessaoRef.current = null;
    setSessaoPortal(null);
  };
  
  // Estados das submissões
  const [minhasSubmissoes, setMinhasSubmissoes] = useState<MinhaSubmissao[]>([]);
  const [submissaoSelecionada, setSubmissaoSelecionada] = useState<SubmissaoData | null>(null);
  
  // Estados do formulário
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [errosCampos, setErrosCampos] = useState<Record<string, string>>({});
  // Observações por campo de anexo (só usado no portal "Solicitações CD's e Lojas")
  const [observacoesArquivos, setObservacoesArquivos] = useState<Record<string, string>>({});
  const [enviando, setEnviando] = useState(false);
  // Indica se já restauramos rascunho do localStorage (evita autosave antes da hora)
  const draftReadyRef = useRef(false);
  const [submissaoResposta, setSubmissaoResposta] = useState<SubmissaoResposta | null>(null);
  
  // Estados de visibilidade dos campos baseado em automações
  const [camposVisiveis, setCamposVisiveis] = useState<Record<string, boolean>>({});
  // Estados de visibilidade de opções específicas dentro de campos
  // Estrutura: { campoId: { opcao1: true, opcao2: false, ... } }
  const [opcoesVisiveis, setOpcoesVisiveis] = useState<Record<string, Record<string, boolean>>>({});
  
  // Chat
  const [novaMensagem, setNovaMensagem] = useState('');
  const [enviandoMensagem, setEnviandoMensagem] = useState(false);
  const mensagensRef = useRef<HTMLDivElement>(null);
  // Aviso ao vivo de nova resposta do time interno
  const [novaResposta, setNovaResposta] = useState(false);
  const internasCountRef = useRef<number | null>(null);

  // Menu mobile
  const [menuAberto, setMenuAberto] = useState(false);
  
  // E-mail obrigatório para contato/atualizações
  const [emailContato, setEmailContato] = useState('');
  // E-mail mostrado na tela de sucesso: o emailContato é limpo junto com o formulário
  const [emailConfirmacao, setEmailConfirmacao] = useState('');
  const [erroEmailContato, setErroEmailContato] = useState('');

  // Documentos
  const [documentoExpandido, setDocumentoExpandido] = useState<string | null>(null);
  
  // Checkbox multi expandidos (para colapsar listas longas)
  const [checkboxExpandido, setCheckboxExpandido] = useState<Record<string, boolean>>({});

  // ============================================================
  // CORES E ESTILOS
  // ============================================================

  const getCorPrimaria = () => portal?.aparencia?.corPrimaria || '#3B82F6';
  const getCorSecundaria = () => portal?.aparencia?.corSecundaria || '#1E40AF';
  const getCorFundo = () => portal?.aparencia?.corFundo || '#F8FAFC';
  const getCorTexto = () => portal?.aparencia?.corTexto || '#1E293B';
  const getLogo = () => portal?.aparencia?.logo;
  
  // Estilo de fundo dinâmico (suporta cor, gradiente e imagem)
  const getFundoStyle = (): React.CSSProperties => {
    const ap = portal?.aparencia;
    if (ap?.tipoFundo === 'gradiente') {
      return {
        background: `linear-gradient(${ap.gradienteAngulo || 135}deg, ${ap.gradienteCor1 || '#667eea'}, ${ap.gradienteCor2 || '#764ba2'})`,
      };
    }
    if (ap?.tipoFundo === 'imagem' && ap.imagemFundo) {
      return {
        backgroundImage: `url(${ap.imagemFundo})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      };
    }
    return { backgroundColor: getCorFundo() };
  };
  
  // Estilo do header (sólido, gradiente ou transparente)
  const getHeaderStyle = (): React.CSSProperties => {
    const estilo = portal?.aparencia?.estiloHeader || 'gradiente';
    if (estilo === 'transparente') {
      return { backgroundColor: 'rgba(0,0,0,0.15)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' };
    }
    if (estilo === 'gradiente') {
      return { background: `linear-gradient(135deg, ${getCorPrimaria()}, ${getCorSecundaria()})` };
    }
    return { backgroundColor: getCorPrimaria() };
  };
  
  // Estilo dos cards
  const getCardClass = () => {
    const estilo = portal?.aparencia?.estiloCards || 'elevado';
    switch (estilo) {
      case 'glass': return 'bg-white/70 backdrop-blur-xl border border-white/30 shadow-xl';
      case 'flat': return 'bg-white border border-gray-200';
      case 'elevado': return 'bg-white shadow-xl shadow-gray-200/50 border border-gray-100';
      default: return 'bg-white rounded-xl shadow-lg';
    }
  };

  // ============================================================
  // EFEITOS
  // ============================================================

  useEffect(() => {
    // Se tem parâmetros de acompanhamento na URL, carregar direto
    if (submissaoIdParam && (tokenAcessoParam || sessaoRef.current)) {
      carregarAcompanhamento(submissaoIdParam, tokenAcessoParam || '');
    } else {
      carregarPortal();
    }
  }, [token, submissaoIdParam, tokenAcessoParam]);

  // 🔐 Com sessão aberta: mostra o e-mail no topo e trava o e-mail do
  // formulário no e-mail provado (o servidor também força isso).
  useEffect(() => {
    if (!exigeCodigo || !sessaoPortal?.email) return;
    if (!usuarioEmail) setUsuarioEmail(sessaoPortal.email);
    if (emailContato !== sessaoPortal.email) setEmailContato(sessaoPortal.email);
  }, [exigeCodigo, sessaoPortal, usuarioEmail, emailContato]);

  useEffect(() => {
    // Scroll para última mensagem
    if (mensagensRef.current) {
      mensagensRef.current.scrollTop = mensagensRef.current.scrollHeight;
    }
  }, [submissaoSelecionada?.mensagens]);

  // Polling: enquanto a pessoa está vendo os detalhes, busca novas mensagens
  useEffect(() => {
    if (telaAtual !== 'detalhes' || !submissaoSelecionada?.id) return;

    const subId = submissaoSelecionada.id;
    const tkn = tokenAcessoParam || submissaoSelecionada.tokenAcesso;
    if (!tkn && !sessaoRef.current) return;

    const interval = setInterval(() => {
      carregarAcompanhamento(subId, tkn || '', true);
    }, 10000);

    return () => clearInterval(interval);
  }, [telaAtual, submissaoSelecionada?.id]);

  // Executar automações quando formData muda (com debounce para não perder foco dos inputs)
  // Lógica alinhada com automationEngine.ts (resolveThen/resolveElse)
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  
  useEffect(() => {
    if (!portal?.automacoes?.length) {
      return;
    }
    
    const timer = setTimeout(() => {
      const currentFormData = formDataRef.current;
    
    // Mapear IDs originais da etapa → IDs do portal
    // Automações referenciam campo IDs originais (ex: campo_mmwh7i98_jrtyvp)
    // Portal pode ter IDs importados (ex: importado_campo_mmwh7i98_jrtyvp_1773946920035)
    const originalToPortalId: Record<string, string> = {};
    portal.campos?.forEach((campo) => {
      const origId = (campo as any).campoOriginalId;
      if (origId && origId !== campo.id) {
        originalToPortalId[origId] = campo.id;
      }
    });
    
    // Função para resolver ID (tenta portal ID, senão usa original)
    const resolveId = (id: string): string => originalToPortalId[id] || id;
    
    const novaVisibilidade: Record<string, boolean> = {};
    const novasOpcoesVisiveis: Record<string, Record<string, boolean>> = {};
    
    const aplicarAlvos = (alvos: string[], tipoAcao: string, _contexto: string, elementos?: any[]) => {
      const mostrar = tipoAcao === 'mostrar';
      alvos.forEach((campoAlvo) => {
        if (!campoAlvo) return;
        const portalAlvoId = resolveId(campoAlvo);
        const elemento = elementos?.find((e: any) => e?.id === campoAlvo);
        if (elemento?.opcoesEspecificas?.length > 0) {
          // __default define o comportamento das opções NÃO listadas (inverso da ação)
          novasOpcoesVisiveis[portalAlvoId] = { __default: !mostrar };
          elemento.opcoesEspecificas.forEach((opcao: string) => {
            novasOpcoesVisiveis[portalAlvoId][opcao] = mostrar;
          });
        } else {
          novaVisibilidade[portalAlvoId] = mostrar;
        }
      });
    };
    
    portal.automacoes.forEach(automacao => {
      automacao.acoes.forEach(acao => {
        if (acao.tipo !== 'exibir_ocultar') return;
        
        const regras = acao.configuracao?.regras || [];
        
        regras.forEach((regra: any) => {
          const condicao = regra.condicao;
          const campoIds = condicao?.campoIds || (condicao?.campoId ? [condicao.campoId] : []);
          
          // Avaliar condição principal - todos os campoIds devem atender (AND)
          let condicaoAtendida = campoIds.length > 0;
          for (const cId of campoIds) {
            const portalId = resolveId(cId);
            const valorCampo = currentFormData[portalId];
            if (!avaliarCondicaoAutomacao(valorCampo, condicao?.operador || '', condicao?.valor)) {
              condicaoAtendida = false;
              break;
            }
          }
          
          // Avaliar condições E (AND) e OU (OR) adicionais
          if (regra.condicoesAdicionais?.length > 0) {
            // Separar condições AND e OR
            const condicoesAND = regra.condicoesAdicionais.filter((c: any) => c.tipoLogico !== 'OU');
            const condicoesOU = regra.condicoesAdicionais.filter((c: any) => c.tipoLogico === 'OU');
            
            // Todas as condições AND devem ser verdadeiras
            if (condicaoAtendida) {
              for (const condE of condicoesAND) {
                if (condE.campoId) {
                  const valorE = currentFormData[resolveId(condE.campoId)];
                  if (!avaliarCondicaoAutomacao(valorE, condE.operador, condE.valor)) {
                    condicaoAtendida = false;
                    break;
                  }
                }
              }
            }
            
            // Se há condições OU: condição principal (com ANDs) OU qualquer OU deve ser verdadeira
            if (condicoesOU.length > 0) {
              let algumOUAtendido = false;
              for (const condOU of condicoesOU) {
                if (condOU.campoId) {
                  const valorOU = currentFormData[resolveId(condOU.campoId)];
                  if (avaliarCondicaoAutomacao(valorOU, condOU.operador, condOU.valor)) {
                    algumOUAtendido = true;
                    break;
                  }
                }
              }
              condicaoAtendida = condicaoAtendida || algumOUAtendido;
            }
          }
          
          // Resolver alvos ENTÃO (mesma lógica do automationEngine.resolveThen)
          const resolveThen = () => {
            if (regra.acaoEntao?.campo) {
              return { alvos: [regra.acaoEntao.campo], tipo: regra.acaoEntao.acao || 'mostrar', elementos: [] as any[] };
            }
            const elementos = regra.elementosAfetados || [];
            const alvos = elementos.map((e: any) => e?.id).filter(Boolean);
            if (alvos.length > 0) {
              return { alvos, tipo: regra.acao || regra.comportamento || 'mostrar', elementos };
            }
            return null;
          };
          
          // Resolver alvos SENÃO (mesma lógica do automationEngine.resolveElse)
          const resolveElse = () => {
            if (regra.acaoSenao?.campo) {
              return { alvos: [regra.acaoSenao.campo], tipo: regra.acaoSenao.acao || 'esconder', elementos: [] as any[] };
            }
            const elementosSenao = regra.elementosAfetadosSenao || [];
            const alvosSenao = elementosSenao.map((e: any) => e?.id).filter(Boolean);
            if (alvosSenao.length > 0) {
              const principal = resolveThen();
              const tipo = regra.acaoSenao || (principal?.tipo === 'mostrar' ? 'esconder' : 'mostrar');
              return { alvos: alvosSenao, tipo, elementos: elementosSenao };
            }
            // Fallback: inverte a ação ENTÃO nos mesmos alvos
            const principal = resolveThen();
            if (principal) {
              const tipoInvertido = principal.tipo === 'mostrar' ? 'esconder' : 'mostrar';
              return { alvos: principal.alvos, tipo: tipoInvertido, elementos: principal.elementos };
            }
            return null;
          };
          
          if (condicaoAtendida) {
            const entao = resolveThen();
            if (entao) aplicarAlvos(entao.alvos, entao.tipo, 'ENTAO', entao.elementos);
            regra.acoesEntaoAdicionais?.forEach((extra: any) => {
              if (extra.elemento?.id) aplicarAlvos([extra.elemento.id], extra.acao || 'esconder', 'ENTAO-ADD', [extra.elemento]);
            });
          } else {
            const senao = resolveElse();
            if (senao) aplicarAlvos(senao.alvos, senao.tipo, 'SENAO', senao.elementos);
            regra.acoesSenaoAdicionais?.forEach((extra: any) => {
              if (extra.elemento?.id) aplicarAlvos([extra.elemento.id], extra.acao || 'mostrar', 'SENAO-ADD', [extra.elemento]);
            });
          }
        });
      });
    });
    
    setCamposVisiveis(prev => {
      const prevStr = JSON.stringify(prev);
      const newStr = JSON.stringify(novaVisibilidade);
      return prevStr === newStr ? prev : novaVisibilidade;
    });
    setOpcoesVisiveis(prev => {
      const prevStr = JSON.stringify(prev);
      const newStr = JSON.stringify(novasOpcoesVisiveis);
      return prevStr === newStr ? prev : novasOpcoesVisiveis;
    });
    }, 150); // debounce 150ms
    
    return () => clearTimeout(timer);
  }, [formData, portal?.automacoes]);

  // ============================================================
  // FUNÇÕES DE API
  // ============================================================

  // ─── Rascunho local (autosave) ─────────────────────────────
  // Mantém o que o usuário digitou + anexos já enviados (tempId) em localStorage
  // para sobreviver a recargas (ligação atendida, troca de app, OOM do mobile).
  const draftStorageKey = token ? `portal_draft_v1_${token}` : '';

  const isTempUpload = (v: any): boolean =>
    !!v && typeof v === 'object' && v.__temp === true;

  const serializeForDraft = (val: any): any => {
    if (val instanceof File) return undefined; // File não persiste
    if (Array.isArray(val)) {
      const arr = val.map(serializeForDraft).filter(v => v !== undefined);
      return arr.length ? arr : undefined;
    }
    return val;
  };

  const salvarRascunho = useCallback(() => {
    if (!draftStorageKey || !draftReadyRef.current) return;
    try {
      const dadosSerial: Record<string, any> = {};
      Object.entries(formData).forEach(([k, v]) => {
        const s = serializeForDraft(v);
        if (s !== undefined && s !== null && s !== '') dadosSerial[k] = s;
      });
      // Formulário vazio (recém-aberto ou limpo após o envio): não deixa rascunho para trás
      const temConteudo = Object.values(dadosSerial).some(v => v !== false)
        || !!emailContato.trim()
        || Object.values(observacoesArquivos || {}).some(v => !!String(v || '').trim());
      if (!temConteudo) {
        localStorage.removeItem(draftStorageKey);
        return;
      }
      const payload = {
        formData: dadosSerial,
        observacoesArquivos,
        emailContato,
        savedAt: Date.now(),
      };
      localStorage.setItem(draftStorageKey, JSON.stringify(payload));
    } catch (e) {
      // localStorage pode estar cheio / desabilitado; ignorar silenciosamente
      console.warn('[PORTAL] autosave falhou:', e);
    }
  }, [draftStorageKey, formData, observacoesArquivos, emailContato]);

  const limparRascunho = () => {
    if (!draftStorageKey) return;
    try { localStorage.removeItem(draftStorageKey); } catch {}
  };

  // 📎 Reenvio de anexo: arquivos que o solicitante está mandando de novo,
  // por campo pedido pela equipe interna.
  const [arquivosReenvio, setArquivosReenvio] = useState<Record<string, any[]>>({});
  const [enviandoReenvio, setEnviandoReenvio] = useState(false);
  const [erroReenvio, setErroReenvio] = useState<string | null>(null);
  const [reenvioConcluido, setReenvioConcluido] = useState<string | null>(null);
  const [decisaoAprovacao, setDecisaoAprovacao] = useState<'Sim' | 'Não' | ''>('');
  const [comentarioAprovacao, setComentarioAprovacao] = useState('');
  const [enviandoAprovacao, setEnviandoAprovacao] = useState(false);
  const [erroAprovacao, setErroAprovacao] = useState<string | null>(null);
  const [aprovacaoConcluida, setAprovacaoConcluida] = useState<string | null>(null);

  // Sobe o arquivo do reenvio para a área temporária, igual ao formulário novo.
  const uploadArquivoReenvio = async (campoId: string, file: File): Promise<void> => {
    const localId = `lid_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const placeholder = {
      __temp: true,
      localId,
      status: 'uploading' as 'uploading' | 'uploaded' | 'error',
      name: file.name,
      size: file.size,
      type: file.type,
    };
    setArquivosReenvio(prev => ({ ...prev, [campoId]: [...(prev[campoId] || []), placeholder] }));

    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      const resp = await fetch(`${API_BASE}/api/portal-publico/${token}/upload-temp`, {
        method: 'POST',
        body: fd,
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Falha no upload');

      setArquivosReenvio(prev => ({
        ...prev,
        [campoId]: (prev[campoId] || []).map((it: any) =>
          it.localId === localId
            ? { ...it, status: 'uploaded', tempId: data.tempId, originalName: data.originalName, size: data.size, type: data.type }
            : it
        ),
      }));
    } catch (err: any) {
      console.error('[PORTAL] upload do reenvio falhou:', err);
      setArquivosReenvio(prev => ({
        ...prev,
        [campoId]: (prev[campoId] || []).map((it: any) =>
          it.localId === localId ? { ...it, status: 'error', erro: err?.message || 'Falha' } : it
        ),
      }));
    }
  };

  const enviarReenvio = async () => {
    const solicitacao = submissaoSelecionada?.solicitacaoReenvio;
    if (!solicitacao || !submissaoSelecionada) return;

    setErroReenvio(null);

    // Só vale arquivo que terminou de subir; o resto o servidor não acha.
    const payload: Record<string, any[]> = {};
    const pendentes: string[] = [];
    for (const campo of solicitacao.campos) {
      const prontos = (arquivosReenvio[campo.campoId] || []).filter((a: any) => a.status === 'uploaded' && a.tempId);
      const minimo = Number(campo.minAnexos || 0) || 1;
      if (prontos.length < minimo) {
        pendentes.push(`${campo.nome}${minimo > 1 ? ` (mínimo ${minimo})` : ''}`);
        continue;
      }
      payload[campo.campoId] = prontos.map((a: any) => ({
        tempId: a.tempId,
        originalName: a.originalName || a.name,
        size: a.size,
        type: a.type,
      }));
    }

    const subindo = Object.values(arquivosReenvio).flat().some((a: any) => a.status === 'uploading');
    if (subindo) {
      setErroReenvio('Aguarde o envio dos arquivos terminar.');
      return;
    }
    if (pendentes.length > 0) {
      setErroReenvio(`Anexe o arquivo de: ${pendentes.join(', ')}`);
      return;
    }

    setEnviandoReenvio(true);
    try {
      const resp = await fetch(`${API_BASE}/api/portal-publico/${token}/reenviar-anexos`, {
        method: 'POST',
        headers: headersPortal(),
        body: JSON.stringify({
          submissaoId: submissaoSelecionada.id,
          tokenAcesso: tokenAccessoDoReenvio(),
          arquivosTemp: payload,
        }),
      });
      const data = await resp.json();
      if (!data.success) {
        setErroReenvio(data.error || 'Não foi possível reenviar os arquivos');
        return;
      }
      setArquivosReenvio({});
      setReenvioConcluido(
        data.etapaDestinoNome
          ? `Arquivos reenviados. Sua solicitação voltou para "${data.etapaDestinoNome}".`
          : 'Arquivos reenviados com sucesso.'
      );
      await carregarAcompanhamento(submissaoSelecionada.id, tokenAccessoDoReenvio() || '', true);
    } catch (err) {
      console.error('[PORTAL] reenvio falhou:', err);
      setErroReenvio('Erro de conexão ao reenviar. Tente novamente.');
    } finally {
      setEnviandoReenvio(false);
    }
  };

  const tokenAccessoDoReenvio = () => tokenAcessoParam || submissaoSelecionada?.tokenAcesso || '';

  const enviarAprovacao = async () => {
    if (!submissaoSelecionada?.aprovacaoSolicitante) return;
    setErroAprovacao(null);
    if (!decisaoAprovacao) {
      setErroAprovacao('Escolha se aprova ou não.');
      return;
    }
    if (decisaoAprovacao === 'Não' && !comentarioAprovacao.trim()) {
      setErroAprovacao('Conte nos comentários o que precisa mudar.');
      return;
    }
    setEnviandoAprovacao(true);
    try {
      const resp = await fetch(`${API_BASE}/api/portal-publico/${token}/aprovar-entrega`, {
        method: 'POST',
        headers: headersPortal(),
        body: JSON.stringify({
          submissaoId: submissaoSelecionada.id,
          tokenAcesso: tokenAccessoDoReenvio(),
          decisao: decisaoAprovacao,
          comentario: comentarioAprovacao.trim(),
        }),
      });
      const data = await resp.json();
      if (!data.success) {
        setErroAprovacao(data.error || 'Não foi possível salvar sua resposta');
        return;
      }
      setDecisaoAprovacao('');
      setComentarioAprovacao('');
      setAprovacaoConcluida(
        data.decisao === 'Sim'
          ? 'Obrigado! Você aprovou a entrega.'
          : 'Obrigado! Sua resposta foi enviada e a equipe vai ajustar.'
      );
      await carregarAcompanhamento(submissaoSelecionada.id, tokenAccessoDoReenvio() || '', true);
    } catch (err) {
      console.error('[PORTAL] aprovação falhou:', err);
      setErroAprovacao('Erro de conexão. Tente novamente.');
    } finally {
      setEnviandoAprovacao(false);
    }
  };

  // Upload imediato de um arquivo para a área temporária do servidor.
  // Atualiza o formData incrementalmente (status uploading -> uploaded/error).
  const uploadArquivoTemp = async (campoId: string, file: File): Promise<void> => {
    const localId = `lid_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const placeholder = {
      __temp: true,
      localId,
      status: 'uploading' as 'uploading' | 'uploaded' | 'error',
      name: file.name,
      size: file.size,
      type: file.type,
      progress: 0,
    };
    // Adicionar placeholder
    setFormData(prev => {
      const atual = prev[campoId];
      const lista = Array.isArray(atual) ? [...atual] : (atual ? [atual] : []);
      lista.push(placeholder);
      return { ...prev, [campoId]: lista };
    });

    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      const resp = await fetch(`${API_BASE}/api/portal-publico/${token}/upload-temp`, {
        method: 'POST',
        body: fd,
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Falha no upload');

      setFormData(prev => {
        const atual = prev[campoId];
        if (!Array.isArray(atual)) return prev;
        return {
          ...prev,
          [campoId]: atual.map((it: any) =>
            isTempUpload(it) && it.localId === localId
              ? { ...it, status: 'uploaded', tempId: data.tempId, originalName: data.originalName, size: data.size, type: data.type }
              : it
          ),
        };
      });
    } catch (err: any) {
      console.error('[PORTAL] upload-temp falhou:', err);
      setFormData(prev => {
        const atual = prev[campoId];
        if (!Array.isArray(atual)) return prev;
        return {
          ...prev,
          [campoId]: atual.map((it: any) =>
            isTempUpload(it) && it.localId === localId
              ? { ...it, status: 'error', erro: err?.message || 'Falha' }
              : it
          ),
        };
      });
    }
  };

  // Restaurar rascunho do localStorage assim que o portal carrega (modo formulário novo)
  useEffect(() => {
    if (!portal || submissaoIdParam) return; // só em modo "nova submissão"
    if (draftReadyRef.current) return;
    try {
      if (draftStorageKey) {
        const raw = localStorage.getItem(draftStorageKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') {
            if (parsed.formData && typeof parsed.formData === 'object') {
              setFormData(prev => ({ ...prev, ...parsed.formData }));
            }
            if (parsed.observacoesArquivos && typeof parsed.observacoesArquivos === 'object') {
              setObservacoesArquivos(parsed.observacoesArquivos);
            }
            if (parsed.emailContato) setEmailContato(parsed.emailContato);
          }
        }
      }
    } catch (e) {
      console.warn('[PORTAL] restauração de rascunho falhou:', e);
    } finally {
      draftReadyRef.current = true;
    }
  }, [portal, submissaoIdParam, draftStorageKey]);

  // Autosave a cada mudança relevante (debounced 500ms)
  useEffect(() => {
    if (!draftReadyRef.current) return;
    const id = setTimeout(() => salvarRascunho(), 500);
    return () => clearTimeout(id);
  }, [formData, observacoesArquivos, emailContato, salvarRascunho]);

  const carregarPortal = async () => {
    if (!token) {
      setError('Link inválido');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/portal-publico/${token}`);
      const data = await response.json();

      // DEBUG: Ver EXATAMENTE quais campos estão vindo
      console.log('🔍 CAMPOS RECEBIDOS DA API:', data.campos);
      console.log('🔍 Cada campo:', data.campos?.map((c: any) => ({
        nome: c.nome,
        id: c.id,
        placeholder: c.placeholder,
        campoOriginalId: c.campoOriginalId
      })));

      // Cotação é outro tipo de link: parte de um card que já existe, então tem
      // tela própria em vez do formulário em branco deste portal.
      if (data.success && data.tipo === 'cotacao') {
        setDadosCotacao(data);
      } else if (data.success) {
        setPortal(data.portal);
        inicializarFormulario(data.portal);
        setTelaAtual('inicio');
      } else {
        setError(data.error || 'Portal não encontrado');
      }
    } catch (err) {
      console.error('Erro ao carregar portal:', err);
      setError('Erro ao carregar portal. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const inicializarFormulario = (portalData: PortalPublicoData) => {
    const initialData: Record<string, any> = {};
    const visibilidadeInicial: Record<string, boolean> = {};
    
    // Mapear IDs originais da etapa → IDs do portal (para automações)
    const originalToPortalId: Record<string, string> = {};
    portalData.campos?.forEach((campo) => {
      const origId = (campo as any).campoOriginalId;
      if (origId && origId !== campo.id) {
        originalToPortalId[origId] = campo.id;
      }
    });
    const resolveId = (id: string): string => originalToPortalId[id] || id;
    
    // Identificar campos controlados por automações (converte IDs para portal IDs)
    const camposControladosPorAutomacao = new Set<string>();
    portalData.automacoes?.forEach((automacao) => {
      automacao.acoes?.forEach((acao) => {
        if (acao.tipo === 'exibir_ocultar') {
          acao.configuracao?.regras?.forEach((regra: any) => {
            if (regra.acaoEntao?.campo) camposControladosPorAutomacao.add(resolveId(regra.acaoEntao.campo));
            if (regra.acaoSenao?.campo) camposControladosPorAutomacao.add(resolveId(regra.acaoSenao.campo));
            regra.elementosAfetados?.forEach((el: any) => {
              if (el.id) camposControladosPorAutomacao.add(resolveId(el.id));
            });
            regra.elementosAfetadosSenao?.forEach((el: any) => {
              if (el.id) camposControladosPorAutomacao.add(resolveId(el.id));
            });
            regra.acoesEntaoAdicionais?.forEach((extra: any) => {
              if (extra.elemento?.id) camposControladosPorAutomacao.add(resolveId(extra.elemento.id));
            });
            regra.acoesSenaoAdicionais?.forEach((extra: any) => {
              if (extra.elemento?.id) camposControladosPorAutomacao.add(resolveId(extra.elemento.id));
            });
          });
        }
      });
    });
    
    portalData.campos?.forEach((campo) => {
      if (campo.tipo === 'checkbox' && campo.opcoes?.length) {
        initialData[campo.id] = [];
      } else if (campo.tipo === 'checkbox') {
        initialData[campo.id] = false;
      } else if (campo.tipo === 'checkbox_single') {
        initialData[campo.id] = '';
      } else if (campo.tipo === 'checkbox_multi') {
        initialData[campo.id] = [];
      } else if (campo.tipo !== 'documento') {
        initialData[campo.id] = '';
      }
      
      if (camposControladosPorAutomacao.has(campo.id)) {
        visibilidadeInicial[campo.id] = false;
      }
    });
    
    setFormData(initialData);
    setCamposVisiveis(visibilidadeInicial);
  };

  const carregarAcompanhamento = async (submissaoId: string, tokenAcesso: string, silencioso = false): Promise<boolean> => {
    if (!token) {
      if (!silencioso) {
        setError('Link inválido');
        setLoading(false);
      }
      return false;
    }

    try {
      if (!silencioso) setLoading(true);
      const response = await fetch(
        `${API_BASE}/api/portal-publico/${token}/acompanhar/${submissaoId}?t=${encodeURIComponent(tokenAcesso || '')}`,
        { headers: headersPortal(false) }
      );
      const data = await response.json();

      // 🔐 Portal com acesso por código e sem sessão válida: pede o código e
      // volta para esta solicitação depois de confirmado.
      if (data.precisaCodigo) {
        encerrarSessaoLocal();
        if (!silencioso) {
          submissaoPendenteRef.current = submissaoId;
          await carregarPortal();
          setEtapaCodigo('email');
          setTelaAtual('login');
        }
        return false;
      }

      if (data.success) {
        setPortal(data.portal);
        const mensagens = data.mensagens || data.submissao?.mensagens || [];
        const submissao = {
          ...data.submissao,
          mensagens,
          etapaAtual: data.etapaAtual || data.submissao?.etapaAtual,
          totalMensagens: mensagens.length,
          mensagensNaoLidas: 0,
          tokenAcesso: tokenAcesso
        };

        // Detectar resposta nova do time interno (badge ao vivo)
        const internasCount = mensagens.filter((m: PortalMensagem) => m.origem === 'interno').length;
        if (internasCountRef.current !== null && internasCount > internasCountRef.current) {
          setNovaResposta(true);
        }
        internasCountRef.current = internasCount;

        setSubmissaoSelecionada(submissao);
        setUsuarioEmail(submissao.emailExterno);
        if (!silencioso) setTelaAtual('detalhes');
        return true;
      } else if (!silencioso) {
        setError(data.error || 'Não foi possível carregar o acompanhamento');
      }
      return false;
    } catch (err) {
      console.error('Erro ao carregar acompanhamento:', err);
      if (!silencioso) setError('Erro ao carregar acompanhamento. Tente novamente.');
      return false;
    } finally {
      if (!silencioso) setLoading(false);
    }
  };

  // 🔐 Lista as solicitações do e-mail da sessão (portal com acesso por código)
  const carregarMinhasSolicitacoes = async (): Promise<boolean> => {
    const response = await fetch(`${API_BASE}/api/portal-publico/${token}/minhas-submissoes`, {
      method: 'POST',
      headers: headersPortal(),
      body: JSON.stringify({})
    });
    const data = await response.json();
    if (data.precisaCodigo) {
      encerrarSessaoLocal();
      return false;
    }
    if (!data.success) {
      setError(data.error || 'Não foi possível carregar suas solicitações');
      return false;
    }
    setMinhasSubmissoes(data.submissoes || []);
    setUsuarioEmail(data.email || sessaoRef.current?.email || null);
    return true;
  };

  const irParaAcompanhar = async () => {
    if (exigeCodigo && sessaoRef.current) {
      setError(null);
      try {
        if (await carregarMinhasSolicitacoes()) {
          setTelaAtual('dashboard');
          return;
        }
      } catch {
        // sem conexão: cai na tela de e-mail e código
      }
    }
    setEtapaCodigo('email');
    setTelaAtual('login');
  };

  const pedirCodigo = async () => {
    const email = emailLogin.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Informe um e-mail válido');
      return;
    }
    setLoginLoading(true);
    setError(null);
    setAvisoLogin(null);
    try {
      const response = await fetch(`${API_BASE}/api/portal-publico/${token}/acesso/codigo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      if (data.success) {
        setCodigoLogin('');
        setEtapaCodigo('codigo');
        setAvisoLogin(`Enviamos um código de 6 dígitos para ${email}. Ele vale por ${data.validadeMinutos || 10} minutos.`);
      } else {
        setError(data.error || 'Não foi possível enviar o código');
      }
    } catch (err) {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setLoginLoading(false);
    }
  };

  const validarCodigo = async () => {
    const email = emailLogin.trim().toLowerCase();
    if (codigoLogin.length !== 6) {
      setError('Digite o código de 6 dígitos');
      return;
    }
    setLoginLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/portal-publico/${token}/acesso/validar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, codigo: codigoLogin })
      });
      const data = await response.json();
      if (!data.success) {
        setError(data.error || 'Código incorreto');
        return;
      }
      const nova = { sessao: data.sessao, email: data.email, expiraEm: data.expiraEm };
      try { sessionStorage.setItem(sessaoStorageKey, JSON.stringify(nova)); } catch {}
      sessaoRef.current = nova;
      setSessaoPortal(nova);
      setCodigoLogin('');
      setEtapaCodigo('email');
      setAvisoLogin(null);

      await carregarMinhasSolicitacoes();
      const pendente = submissaoPendenteRef.current;
      submissaoPendenteRef.current = null;
      const abriu = pendente ? await carregarAcompanhamento(pendente, '') : false;
      if (!abriu) setTelaAtual('dashboard');
    } catch (err) {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setLoginLoading(false);
    }
  };

  const fazerLogin = async () => {
    if (!emailLogin && !protocoloLogin) {
      setError('Informe seu e-mail ou número do protocolo');
      return;
    }

    setLoginLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/portal-publico/${token}/minhas-submissoes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailLogin,
          protocolo: protocoloLogin
        })
      });

      const data = await response.json();

      if (data.success) {
        setMinhasSubmissoes(data.submissoes);
        setUsuarioEmail(data.email || emailLogin);
        setTelaAtual('dashboard');
      } else {
        setError(data.error || 'Nenhuma solicitação encontrada');
      }
    } catch (err) {
      console.error('Erro ao buscar submissões:', err);
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setLoginLoading(false);
    }
  };

  const abrirDetalhesSubmissao = async (submissao: MinhaSubmissao) => {
    if (!token || (!submissao.tokenAcesso && !sessaoRef.current)) return;
    
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE}/api/portal-publico/${token}/acompanhar/${submissao.id}?t=${encodeURIComponent(submissao.tokenAcesso || '')}`,
        { headers: headersPortal(false) }
      );
      const data = await response.json();

      // 🔐 sessão venceu no meio do uso: pede o código de novo
      if (data.precisaCodigo) {
        encerrarSessaoLocal();
        submissaoPendenteRef.current = submissao.id;
        setEtapaCodigo('email');
        setTelaAtual('login');
        return;
      }

      if (data.success) {
        setPortal(data.portal);
        const sub = {
          ...data.submissao,
          mensagens: data.mensagens || data.submissao?.mensagens || [],
          etapaAtual: data.etapaAtual || data.submissao?.etapaAtual,
          totalMensagens: data.mensagens?.length || 0,
          mensagensNaoLidas: 0,
          tokenAcesso: submissao.tokenAcesso
        };
        setSubmissaoSelecionada(sub);
        setTelaAtual('detalhes');
        
        // Atualizar URL
        setSearchParams(submissao.tokenAcesso
          ? { submissaoId: submissao.id, t: submissao.tokenAcesso }
          : { submissaoId: submissao.id });
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Erro ao carregar detalhes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar e-mail de contato obrigatório
    if (!emailContato.trim()) {
      setErroEmailContato('E-mail é obrigatório para receber atualizações');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailContato)) {
      setErroEmailContato('E-mail inválido');
      return;
    }
    setErroEmailContato('');
    
    if (!validarFormulario()) return;

    setEnviando(true);
    setError(null);
    
    try {
      // Separar (a) arquivos File legados, (b) anexos já enviados via /upload-temp, (c) dados normais
      const dadosNormais: Record<string, any> = {};
      const arquivos: Record<string, File[]> = {};
      const arquivosTemp: Record<string, Array<{ tempId: string; originalName: string; size: number; type: string }>> = {};
      let aindaEnviando = false;
      let comErro = false;
      
      Object.entries(formData).forEach(([key, value]) => {
        const lista = Array.isArray(value) ? value : (value !== undefined && value !== null ? [value] : []);
        const files: File[] = [];
        const temps: any[] = [];
        const outros: any[] = [];
        lista.forEach(item => {
          if (item instanceof File) files.push(item);
          else if (item && typeof item === 'object' && item.__temp === true) {
            if (item.status === 'uploading') aindaEnviando = true;
            else if (item.status === 'error') comErro = true;
            else if (item.status === 'uploaded' && item.tempId) {
              temps.push({
                tempId: item.tempId,
                originalName: item.originalName || item.name,
                size: item.size,
                type: item.type,
              });
            }
          } else {
            outros.push(item);
          }
        });
        if (files.length) arquivos[key] = files;
        if (temps.length) arquivosTemp[key] = temps;
        // dados normais: se a entrada original NÃO era um array de anexos, manter como estava
        const eraArrayAnexos = lista.length > 0 && lista.every(it => it instanceof File || (it && typeof it === 'object' && it.__temp === true));
        if (!eraArrayAnexos) {
          dadosNormais[key] = value;
        } else if (outros.length) {
          dadosNormais[key] = outros.length === 1 ? outros[0] : outros;
        }
      });

      if (aindaEnviando) {
        setError('Aguarde o envio dos anexos terminar antes de enviar o formulário.');
        setEnviando(false);
        return;
      }
      if (comErro) {
        setError('Há anexos que falharam ao enviar. Remova-os ou tente novamente.');
        setEnviando(false);
        return;
      }

      // Observações por campo de anexo (só no portal checklist CDs/Lojas):
      // são concatenadas e anexadas ao campo "Informações adicionais" (último texto_longo) do formulário.
      const portalNome = (portal?.nome || '').toLowerCase();
      const isChecklistLojas = portalNome.includes('cd') && portalNome.includes('loja');
      if (isChecklistLojas) {
        const linhasObs: string[] = [];
        (portal?.campos || []).forEach(c => {
          const obs = observacoesArquivos[c.id];
          const temAnexo = (arquivos[c.id]?.length || 0) + (arquivosTemp[c.id]?.length || 0) > 0;
          if (obs && obs.trim() && temAnexo) {
            linhasObs.push(`Observação de ${String(c.nome || c.id).toLowerCase()}: ${obs.trim()}`);
          }
        });
        if (linhasObs.length > 0) {
          // Procurar campo texto_longo/texto cujo nome contenha "inform" (ex: "Informações adicionais")
          const campoInfo = (portal?.campos || []).find(c =>
            (c.tipo === 'texto_longo' || c.tipo === 'texto') &&
            String(c.nome || '').toLowerCase().includes('inform')
          );
          const bloco = linhasObs.join('\n');
          if (campoInfo) {
            const atual = String(dadosNormais[campoInfo.id] || '').trim();
            dadosNormais[campoInfo.id] = atual ? `${atual}\n\n${bloco}` : bloco;
          } else {
            // Fallback: anexar como chave separada (não vai pro card, mas fica na submissão)
            dadosNormais['_observacoesAnexos'] = bloco;
          }
        }
      }
      
      let response: Response;
      
      if (Object.keys(arquivos).length > 0) {
        // Tem arquivos: usar FormData (multipart)
        const fd = new FormData();
        fd.append('json', JSON.stringify({
          dados: dadosNormais,
          emailContato: emailContato.trim().toLowerCase(),
          origem: 'formulario_publico',
          arquivosTemp,
        }));
        Object.entries(arquivos).forEach(([campoId, files]) => {
          files.forEach(file => {
            fd.append(`file_${campoId}`, file, file.name);
          });
        });
        response = await fetch(`${API_BASE}/api/portal-publico/${token}/submeter`, {
          method: 'POST',
          headers: headersPortal(false),
          body: fd
        });
      } else {
        // Sem arquivos: JSON normal (com arquivosTemp se houver)
        response = await fetch(`${API_BASE}/api/portal-publico/${token}/submeter`, {
          method: 'POST',
          headers: headersPortal(),
          body: JSON.stringify({
            dados: dadosNormais,
            emailContato: emailContato.trim().toLowerCase(),
            origem: 'formulario_publico',
            arquivosTemp,
          })
        });
      }

      const data: SubmissaoResposta = await response.json();

      if (data.success) {
        // Enviou: nada da solicitação fica na tela nem no navegador. Os botões
        // "Nova Solicitação" do topo/menu só trocam de tela, e sem isto a próxima
        // pessoa (computador de loja, balcão) abria o formulário com os documentos
        // e dados de quem enviou antes — e podia mandá-los de novo num card novo.
        setEmailConfirmacao(emailContato.trim().toLowerCase());
        if (portal) inicializarFormulario(portal);
        setObservacoesArquivos({});
        setEmailContato('');
        limparRascunho();
        setSubmissaoResposta(data);
        setTelaAtual('sucesso');
        // 🔐 com sessão aberta, a lista já passa a incluir a solicitação nova
        if (exigeCodigo && sessaoRef.current) {
          carregarMinhasSolicitacoes().catch(() => {});
        }
      } else {
        setError(data.error || 'Erro ao enviar formulário');
      }
    } catch (err) {
      console.error('Erro ao enviar:', err);
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  const enviarMensagem = async () => {
    if (!novaMensagem.trim() || !submissaoSelecionada) return;

    setEnviandoMensagem(true);
    try {
      const response = await fetch(`${API_BASE}/api/portal-publico/${token}/mensagem`, {
        method: 'POST',
        headers: headersPortal(),
        body: JSON.stringify({
          submissaoId: submissaoSelecionada.id,
          tokenAcesso: tokenAcessoParam || submissaoSelecionada.tokenAcesso,
          mensagem: novaMensagem.trim()
        })
      });

      const data = await response.json();

      if (data.success) {
        setNovaMensagem('');
        // Recarregar mensagens
        const sub = submissaoSelecionada;
        const tkn = tokenAcessoParam || sub.tokenAcesso || '';
        if (tkn || sessaoRef.current) {
          await carregarAcompanhamento(sub.id, tkn);
        }
      } else {
        setError(data.error || 'Erro ao enviar mensagem');
      }
    } catch (err) {
      setError('Erro ao enviar mensagem');
    } finally {
      setEnviandoMensagem(false);
    }
  };

  // ============================================================
  // FUNÇÕES AUXILIARES
  // ============================================================

  const validarFormulario = (): boolean => {
    const erros: Record<string, string> = {};
    
    portal?.campos?.forEach((campo) => {
      // Verificar se campo está visível
      if (camposVisiveis[campo.id] === false) return;
      
      if (campo.obrigatorio) {
        const valor = formData[campo.id];
        if (valor === undefined || valor === null || valor === '' || (Array.isArray(valor) && valor.length === 0)) {
          erros[campo.id] = 'Campo obrigatório';
        }
      }

      // 📎 Quantidade mínima de anexos (ex.: 3 referências comerciais)
      if ((campo.tipo as string) === 'anexo' || (campo.tipo as string) === 'arquivo') {
        const min = Number(campo.minAnexos || 0);
        if (min > 0) {
          const bruto = formData[campo.id];
          const lista: any[] = Array.isArray(bruto) ? bruto : bruto ? [bruto] : [];
          // só conta o que realmente subiu (ignora upload em andamento / com erro)
          const enviados = lista.filter(a => !(a && typeof a === 'object' && a.__temp === true && a.status !== 'uploaded'));
          if (enviados.length < min) {
            erros[campo.id] = `Envie pelo menos ${min} arquivo${min > 1 ? 's' : ''} (você anexou ${enviados.length}).`;
          }
        }
      }

      const valor = formData[campo.id];
      if (valor) {
        if (campo.tipo === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(valor)) {
          erros[campo.id] = 'E-mail inválido';
        }
        if (campo.tipo === 'telefone' && !/^\d{10,11}$/.test(valor.replace(/\D/g, ''))) {
          erros[campo.id] = 'Telefone inválido';
        }
        if (campo.tipo === 'cpf_cnpj') {
          const numeros = valor.replace(/\D/g, '');
          if (numeros.length !== 11 && numeros.length !== 14) {
            erros[campo.id] = 'CPF ou CNPJ inválido';
          }
        }
      }
    });

    setErrosCampos(erros);
    return Object.keys(erros).length === 0;
  };

  const handleChange = (campoId: string, valor: any) => {
    setFormData(prev => ({ ...prev, [campoId]: valor }));
    if (errosCampos[campoId]) {
      setErrosCampos(prev => ({ ...prev, [campoId]: '' }));
    }
  };

  // Limpar um campo e todos os campos abaixo dele na ordem do formulário
  const handleLimparCascata = (campoId: string) => {
    if (!portal?.campos) return;
    
    // Filtrar campos visíveis (mesma lógica do render)
    const camposFiltrados = portal.campos.filter(c => {
      const nomeNorm = (c.nome || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\*/g, '')
        .trim();
      return nomeNorm !== 'titulo' && c.campoOriginalId !== 'titulo';
    });

    const idx = camposFiltrados.findIndex(c => c.id === campoId);
    if (idx === -1) return;

    // Pegar todos os campos a partir deste (inclusive)
    const camposParaLimpar = camposFiltrados.slice(idx);

    setFormData(prev => {
      const novoFormData = { ...prev };
      for (const campo of camposParaLimpar) {
        const valorAtual = novoFormData[campo.id];
        if (valorAtual !== undefined && valorAtual !== '' && !(Array.isArray(valorAtual) && valorAtual.length === 0)) {
          novoFormData[campo.id] = Array.isArray(valorAtual) ? [] : '';
        }
      }
      return novoFormData;
    });

    // Limpar erros dos campos afetados
    setErrosCampos(prev => {
      const novosErros = { ...prev };
      for (const campo of camposParaLimpar) {
        if (novosErros[campo.id]) {
          novosErros[campo.id] = '';
        }
      }
      return novosErros;
    });
  };

  const avaliarCondicaoAutomacao = (valorCampo: any, operador: string, valorEsperado?: string | number): boolean => {
    switch (operador) {
      case 'igual':
        if (Array.isArray(valorCampo)) return valorCampo.includes(String(valorEsperado));
        return String(valorCampo) === String(valorEsperado);
      case 'diferente':
        if (Array.isArray(valorCampo)) return !valorCampo.includes(String(valorEsperado));
        return String(valorCampo) !== String(valorEsperado);
      case 'contem':
        if (Array.isArray(valorCampo)) return valorCampo.some(v => String(v).toLowerCase().includes(String(valorEsperado).toLowerCase()));
        return String(valorCampo).toLowerCase().includes(String(valorEsperado).toLowerCase());
      case 'preenchido':
        if (Array.isArray(valorCampo)) return valorCampo.length > 0;
        return valorCampo !== undefined && valorCampo !== null && valorCampo !== '';
      case 'vazio':
        if (Array.isArray(valorCampo)) return valorCampo.length === 0;
        return valorCampo === undefined || valorCampo === null || valorCampo === '';
      case 'maior':
        return Number(valorCampo) > Number(valorEsperado);
      case 'menor':
        return Number(valorCampo) < Number(valorEsperado);
      default:
        return false;
    }
  };

  // Função auxiliar para verificar se uma opção específica está visível
  const isOpcaoVisivel = (campoId: string, opcao: string): boolean => {
    const opcoesControladas = opcoesVisiveis[campoId];
    // Se não há controle específico para este campo, todas as opções são visíveis
    if (!opcoesControladas) return true;
    // Se a opção está no controle, retorna o valor controlado
    if (opcoesControladas[opcao] !== undefined) return opcoesControladas[opcao];
    // Opção não listada: usa __default (ex: "Mostrar" específicas → ocultar as demais)
    if (opcoesControladas['__default'] !== undefined) return opcoesControladas['__default'];
    return true;
  };
  
  // Função auxiliar para filtrar opções visíveis de um campo
  const getOpcoesVisiveis = (campoId: string, opcoes: string[] | undefined): string[] => {
    if (!opcoes) return [];
    return opcoes.filter(opcao => isOpcaoVisivel(campoId, opcao));
  };

  // Verificar se um campo deve estar visível
  // 1. Verifica automações de exibir/ocultar
  // 2. Verifica campoVinculadoId (campo depende de opção específica ser selecionada)
  const campoVisivelPorAutomacao = (campo: PortalCampoConfig | string): boolean => {
    const campoId = typeof campo === 'string' ? campo : campo.id;
    const campoObj = typeof campo === 'string' 
      ? portal?.campos?.find(c => c.id === campoId) 
      : campo;
    
    // 1. Verificar automação de exibir_ocultar - se o campo está no state, usar esse valor
    if (camposVisiveis.hasOwnProperty(campoId)) {
      return camposVisiveis[campoId];
    }
    
    // 1.5 Se o campo tem controle de opções visíveis (opcoesEspecificas nas regras)
    // e TODAS as opções estão ocultas, ocultar o campo inteiro
    if (opcoesVisiveis[campoId] && campoObj) {
      const opcoesCampo = (campoObj as any).opcoes;
      if (opcoesCampo?.length > 0) {
        const opcoesVis = getOpcoesVisiveis(campoId, opcoesCampo);
        if (opcoesVis.length === 0) {
          return false;
        }
      }
    }
    
    // 2. Verificar campoVinculadoId (campo depende de checkbox/seleção)
    if (campoObj && (campoObj as any).campoVinculadoId) {
      const campoVinculadoId = (campoObj as any).campoVinculadoId;
      const valorCampoVinculado = formData[campoVinculadoId];
      
      // Se o campo tem valor vinculado específico, verificar se esse valor está selecionado
      if ((campoObj as any).valorVinculado) {
        const valorEsperado = (campoObj as any).valorVinculado;
        if (Array.isArray(valorCampoVinculado)) {
          return valorCampoVinculado.includes(valorEsperado);
        }
        return valorCampoVinculado === valorEsperado;
      }
      
      // Se não tem valor específico, mostrar se o campo vinculado tem algum valor
      if (Array.isArray(valorCampoVinculado)) {
        return valorCampoVinculado.length > 0;
      }
      return !!valorCampoVinculado;
    }
    
    return true;
  };

  const formatarData = (dataStr: string) => {
    // Datas ISO gravadas sem fuso vem do servidor em UTC; sem o 'Z' o
    // navegador as lia como horario local e exibia 3h a mais.
    const bruto = (dataStr || '').trim();
    const temFuso = /(Z|[+-]\d{2}:?\d{2})$/.test(bruto.slice(10));
    const data = new Date(temFuso || !bruto.includes('T') ? bruto : bruto + 'Z');
    if (isNaN(data.getTime())) return dataStr;
    return data.toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { bg: string; text: string; label: string; icon: string }> = {
      pendente: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pendente', icon: '⏳' },
      processado: { bg: 'bg-green-100', text: 'text-green-800', label: 'Processado', icon: '✓' },
      em_andamento: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Em Andamento', icon: '⚡' },
      concluido: { bg: 'bg-green-100', text: 'text-green-800', label: 'Concluído', icon: '✓' },
      erro: { bg: 'bg-red-100', text: 'text-red-800', label: 'Erro', icon: '✕' }
    };
    return configs[status] || configs.pendente;
  };

  const voltarParaDashboard = () => {
    setSubmissaoSelecionada(null);
    setSearchParams({});
    if (usuarioEmail && (minhasSubmissoes.length > 0 || (exigeCodigo && sessaoRef.current))) {
      setTelaAtual('dashboard');
    } else {
      setTelaAtual('inicio');
    }
  };

  const logout = () => {
    // 🔐 encerra a sessão no servidor também (não basta esquecer no navegador)
    if (sessaoRef.current?.sessao) {
      fetch(`${API_BASE}/api/portal-publico/${token}/acesso/sair`, {
        method: 'POST',
        headers: headersPortal()
      }).catch(() => {});
    }
    encerrarSessaoLocal();
    setEtapaCodigo('email');
    setCodigoLogin('');
    setAvisoLogin(null);
    setEmailContato('');
    setUsuarioEmail(null);
    setMinhasSubmissoes([]);
    setSubmissaoSelecionada(null);
    setEmailLogin('');
    setProtocoloLogin('');
    setSearchParams({});
    setTelaAtual('inicio');
  };

  // ============================================================
  // RENDERIZAÇÃO - LOADING E ERRO
  // ============================================================

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: getCorFundo() }}
      >
        <div className="text-center">
          <div 
            className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
            style={{ borderColor: getCorPrimaria() }}
          />
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  if (error && !portal) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Ops!</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // 💰 Link de cotação tem tela própria
  if (dadosCotacao) {
    return <PortalCotacao token={token || ''} apiBase={API_BASE} dados={dadosCotacao} />;
  }

  if (!portal) return null;

  // ============================================================
  // COMPONENTES DE TELA
  // ============================================================

  // Header responsivo
  const Header = () => (
    <header 
      className="sticky top-0 z-50 shadow-lg"
      style={getHeaderStyle()}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer group" onClick={() => setTelaAtual('inicio')}>
            {getLogo() ? (
              <img src={getLogo()} alt={portal.nome} className="h-10 w-auto transition-transform group-hover:scale-105" />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center ring-2 ring-white/30">
                <span className="text-white font-bold text-lg">{portal.nome.charAt(0)}</span>
              </div>
            )}
            <h1 className="text-white font-semibold text-lg hidden sm:block drop-shadow-sm">{portal.nome}</h1>
          </div>
          
          {/* Menu Desktop */}
          <div className="hidden md:flex items-center gap-2">
            {usuarioEmail && (
              <span className="text-white/80 text-sm bg-white/10 px-3 py-1 rounded-full">
                {usuarioEmail}
              </span>
            )}
            {telaAtual !== 'inicio' && telaAtual !== 'login' && (
              <>
                <button
                  onClick={() => setTelaAtual('formulario')}
                  className="px-4 py-2 bg-white/15 hover:bg-white/25 text-white rounded-lg text-sm font-medium transition-all duration-200 backdrop-blur-sm"
                >
                  + Nova Solicitação
                </button>
                {usuarioEmail && (minhasSubmissoes.length > 0 || (exigeCodigo && !!sessaoPortal)) && (
                  <button
                    onClick={() => setTelaAtual('dashboard')}
                    className="px-4 py-2 bg-white/15 hover:bg-white/25 text-white rounded-lg text-sm font-medium transition-all duration-200 backdrop-blur-sm"
                  >
                    Minhas Solicitações
                  </button>
                )}
                {usuarioEmail && (
                  <button
                    onClick={logout}
                    className="px-4 py-2 bg-white/15 hover:bg-white/25 text-white rounded-lg text-sm font-medium transition-all duration-200 backdrop-blur-sm"
                  >
                    Sair
                  </button>
                )}
              </>
            )}
          </div>

          {/* Menu Mobile */}
          <button
            onClick={() => setMenuAberto(!menuAberto)}
            className="md:hidden p-2 text-white rounded-lg hover:bg-white/15 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {menuAberto ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Menu Mobile Expandido */}
        {menuAberto && (
          <div className="md:hidden mt-3 pt-3 border-t border-white/20 space-y-2 animate-fadeIn">
            {usuarioEmail && (
              <p className="text-white/80 text-sm px-2">{usuarioEmail}</p>
            )}
            {telaAtual !== 'inicio' && telaAtual !== 'login' && (
              <>
                <button
                  onClick={() => { setTelaAtual('formulario'); setMenuAberto(false); }}
                  className="w-full px-4 py-2 bg-white/10 text-white rounded-lg text-left text-sm"
                >
                  + Nova Solicitação
                </button>
                {usuarioEmail && (minhasSubmissoes.length > 0 || (exigeCodigo && !!sessaoPortal)) && (
                  <button
                    onClick={() => { setTelaAtual('dashboard'); setMenuAberto(false); }}
                    className="w-full px-4 py-2 bg-white/10 text-white rounded-lg text-left text-sm"
                  >
                    Minhas Solicitações
                  </button>
                )}
                {usuarioEmail && (
                  <button
                    onClick={() => { logout(); setMenuAberto(false); }}
                    className="w-full px-4 py-2 bg-white/10 text-white rounded-lg text-left text-sm"
                  >
                    Sair
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );

  // Tela Inicial
  const TelaInicio = () => (
    <div className="flex-1 flex flex-col">
      {/* Hero Section */}
      <div 
        className="relative py-16 sm:py-24 px-4 overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${getCorPrimaria()}15, ${getCorSecundaria()}10)` }}
      >
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-72 h-72 rounded-full opacity-10 -translate-y-1/2 translate-x-1/3" 
          style={{ background: `radial-gradient(circle, ${getCorPrimaria()}, transparent)` }} />
        <div className="absolute bottom-0 left-0 w-56 h-56 rounded-full opacity-10 translate-y-1/3 -translate-x-1/4" 
          style={{ background: `radial-gradient(circle, ${getCorSecundaria()}, transparent)` }} />
        
        <div className="max-w-lg mx-auto text-center relative z-10">
          {/* Banner image */}
          {portal.aparencia?.imagemBanner && (
            <div className="mb-8 rounded-2xl overflow-hidden shadow-lg mx-auto max-w-md">
              <img 
                src={portal.aparencia.imagemBanner} 
                alt="Banner" 
                className="w-full h-40 sm:h-48 object-cover"
              />
            </div>
          )}
          
          {/* Logo / Avatar */}
          {getLogo() ? (
            <img src={getLogo()} alt={portal.nome} className="h-16 sm:h-20 w-auto mx-auto mb-6 drop-shadow-md" />
          ) : (
            <div 
              className="w-20 h-20 rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-lg"
              style={{ background: `linear-gradient(135deg, ${getCorPrimaria()}, ${getCorSecundaria()})` }}
            >
              <span className="text-white font-bold text-3xl drop-shadow-sm">{portal.nome.charAt(0)}</span>
            </div>
          )}
          
          <h1 className="text-3xl sm:text-4xl font-extrabold mb-3 tracking-tight" style={{ color: getCorTexto() }}>
            {portal.nome}
          </h1>
          {portal.descricao && (
            <p className="text-gray-500 text-base sm:text-lg mb-10 max-w-md mx-auto leading-relaxed">{portal.descricao}</p>
          )}
          
          <div className="space-y-3 max-w-sm mx-auto">
            <button
              onClick={() => setTelaAtual('formulario')}
              className="w-full py-4 text-white font-semibold rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2"
              style={{ background: `linear-gradient(135deg, ${getCorPrimaria()}, ${getCorSecundaria()})` }}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nova Solicitação
            </button>
            
            <button
              onClick={irParaAcompanhar}
              className="w-full py-4 bg-white/80 backdrop-blur-sm border-2 font-semibold rounded-xl shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2"
              style={{ borderColor: getCorPrimaria() + '40', color: getCorPrimaria() }}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Acompanhar Solicitação
            </button>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="mt-auto py-4 text-center">
        <span className="text-xs text-gray-400">Powered by <strong className="font-medium text-gray-500">Escalone</strong></span>
      </div>
    </div>
  );

  // Tela de Login/Identificação
  const TelaLogin = () => (
    <div className="flex-1 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <button
          onClick={() => setTelaAtual('inicio')}
          className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-900 mb-6 transition-colors group"
        >
          <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm font-medium">Voltar</span>
        </button>

        <div className={`${getCardClass()} rounded-2xl overflow-hidden`}>
          <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${getCorPrimaria()}, ${getCorSecundaria()})` }} />
          <div className="p-6 sm:p-8">
            <div className="text-center mb-6">
              <div 
                className="w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${getCorPrimaria()}15, ${getCorSecundaria()}15)` }}
              >
                <svg className="w-7 h-7" style={{ color: getCorPrimaria() }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold" style={{ color: getCorTexto() }}>
                Acompanhar Solicitação
              </h2>
              <p className="text-sm text-gray-400 mt-1">
                {exigeCodigo
                  ? 'Confirme seu e-mail com o código que vamos enviar para ele'
                  : 'Informe seu e-mail ou protocolo'}
              </p>
            </div>
            
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-100 text-red-700 rounded-xl text-sm flex items-center gap-3">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </div>
            )}
            
            {exigeCodigo ? (
              <div className="space-y-4">
                {avisoLogin && (
                  <div className="p-4 bg-blue-50 border border-blue-100 text-blue-800 rounded-xl text-sm">
                    {avisoLogin}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5">
                    Seu e-mail
                  </label>
                  <input
                    type="email"
                    value={emailLogin}
                    disabled={etapaCodigo === 'codigo'}
                    onChange={(e) => { setEmailLogin(e.target.value); setError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && etapaCodigo === 'email') pedirCodigo(); }}
                    placeholder="seu@email.com"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:outline-none transition-all bg-gray-50/50 focus:bg-white disabled:opacity-60"
                    style={{ '--tw-ring-color': getCorPrimaria() + '40' } as any}
                  />
                </div>

                {etapaCodigo === 'codigo' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1.5">
                      Código recebido por e-mail
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={codigoLogin}
                      onChange={(e) => { setCodigoLogin(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(null); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') validarCodigo(); }}
                      placeholder="000000"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:outline-none font-mono text-center text-2xl tracking-widest transition-all bg-gray-50/50 focus:bg-white"
                      style={{ '--tw-ring-color': getCorPrimaria() + '40' } as any}
                    />
                  </div>
                )}

                <button
                  onClick={etapaCodigo === 'email' ? pedirCodigo : validarCodigo}
                  disabled={loginLoading || (etapaCodigo === 'email' ? !emailLogin : codigoLogin.length !== 6)}
                  className="w-full mt-4 py-4 text-white font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
                  style={{ background: `linear-gradient(135deg, ${getCorPrimaria()}, ${getCorSecundaria()})` }}
                >
                  {loginLoading ? 'Aguarde...' : (etapaCodigo === 'email' ? 'Enviar código' : 'Entrar')}
                </button>

                {etapaCodigo === 'codigo' && (
                  <div className="flex items-center justify-between text-sm">
                    <button
                      type="button"
                      onClick={pedirCodigo}
                      disabled={loginLoading}
                      className="text-gray-500 hover:text-gray-900 underline disabled:opacity-50"
                    >
                      Reenviar código
                    </button>
                    <button
                      type="button"
                      onClick={() => { setEtapaCodigo('email'); setCodigoLogin(''); setAvisoLogin(null); setError(null); }}
                      className="text-gray-500 hover:text-gray-900 underline"
                    >
                      Trocar e-mail
                    </button>
                  </div>
                )}
              </div>
            ) : (
            <>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1.5">
                  E-mail cadastrado
                </label>
                <input
                  type="email"
                  value={emailLogin}
                  onChange={(e) => { setEmailLogin(e.target.value); setProtocoloLogin(''); setError(null); }}
                  placeholder="seu@email.com"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:outline-none transition-all bg-gray-50/50 focus:bg-white"
                  style={{ '--tw-ring-color': getCorPrimaria() + '40' } as any}
                />
              </div>
              
              <div className="flex items-center gap-4">
                <div className="flex-1 border-t border-gray-200"></div>
                <span className="text-gray-400 text-xs font-medium uppercase tracking-wider">ou</span>
                <div className="flex-1 border-t border-gray-200"></div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1.5">
                  Número do Protocolo
                </label>
                <input
                  type="text"
                  value={protocoloLogin}
                  onChange={(e) => { setProtocoloLogin(e.target.value.toUpperCase()); setEmailLogin(''); setError(null); }}
                  placeholder="P202601..."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:outline-none font-mono transition-all bg-gray-50/50 focus:bg-white"
                  style={{ '--tw-ring-color': getCorPrimaria() + '40' } as any}
                />
              </div>
            </div>
            
            <button
              onClick={fazerLogin}
              disabled={loginLoading || (!emailLogin && !protocoloLogin)}
              className="w-full mt-8 py-4 text-white font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
              style={{ background: `linear-gradient(135deg, ${getCorPrimaria()}, ${getCorSecundaria()})` }}
            >
              {loginLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white"></div>
                  Buscando...
                </span>
              ) : (
                'Buscar Solicitações'
              )}
            </button>
            </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Dashboard - Lista de Submissões
  const TelaDashboard = () => (
    <div className="flex-1 p-4 py-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold" style={{ color: getCorTexto() }}>
              Minhas Solicitações
            </h2>
            <p className="text-gray-400 text-sm">
              {minhasSubmissoes.length} solicitação(ões) encontrada(s)
            </p>
          </div>
          <button
            onClick={() => setTelaAtual('formulario')}
            className="px-5 py-2.5 text-white font-medium rounded-xl text-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
            style={{ background: `linear-gradient(135deg, ${getCorPrimaria()}, ${getCorSecundaria()})` }}
          >
            + Nova
          </button>
        </div>

        {minhasSubmissoes.length === 0 ? (
          <div className={`${getCardClass()} rounded-2xl p-10 text-center`}>
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-gray-500 font-medium">Nenhuma solicitação encontrada</p>
          </div>
        ) : (
          <div className="space-y-3">
            {minhasSubmissoes.map(sub => {
              const statusConfig = getStatusConfig(sub.status);
              return (
                <div
                  key={sub.id}
                  onClick={() => abrirDetalhesSubmissao(sub)}
                  className={`${getCardClass()} rounded-2xl hover:shadow-xl transition-all duration-300 cursor-pointer p-5 hover:-translate-y-0.5`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        {sub.protocolo && (
                          <span 
                            className="font-mono font-bold text-sm"
                            style={{ color: getCorPrimaria() }}
                          >
                            {sub.protocolo}
                          </span>
                        )}
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${statusConfig.bg} ${statusConfig.text}`}>
                          {statusConfig.icon} {statusConfig.label}
                        </span>
                        {sub.mensagensNaoLidas > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 animate-pulse">
                            💬 {sub.mensagensNaoLidas} nova(s)
                          </span>
                        )}
                      </div>
                      
                      <p className="text-gray-600 text-sm mb-1">
                        Etapa: <span className="font-medium">{sub.etapaNome}</span>
                      </p>
                      
                      <p className="text-gray-400 text-xs">
                        Criado em {formatarData(sub.createdAt)}
                      </p>
                    </div>
                    
                    <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  // Tela de Detalhes da Submissão
  const TelaDetalhes = () => {
    if (!submissaoSelecionada) return null;
    
    const statusConfig = getStatusConfig(submissaoSelecionada.status);
    
    return (
      <div className="flex-1 p-4 pb-20">
        <div className="max-w-3xl mx-auto">
          {/* Voltar */}
          <button
            onClick={voltarParaDashboard}
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-900 mb-4 transition-colors group"
          >
            <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm font-medium">Voltar</span>
          </button>

          {/* ❌ Reprovação: motivo interno aparece só com o rótulo */}
          {submissaoSelecionada.reprovacao && (() => {
            const rep = submissaoSelecionada.reprovacao!;
            const reenvioPendente = submissaoSelecionada.solicitacaoReenvio?.status === 'pendente';
            return (
              <div className="mb-4 rounded-2xl border-2 border-red-300 bg-red-50 px-5 py-4 shadow">
                <div className="flex items-start gap-3">
                  <span className="text-2xl leading-none">❌</span>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-red-800">
                      Reprovado{rep.motivos.length > 0 ? ` — ${rep.motivos.map(m => m.nome).join(', ')}` : ''}
                    </h3>
                    {!reenvioPendente && rep.motivos.some(m => m.detalhe) && (
                      <ul className="mt-2 space-y-1 text-sm text-red-700">
                        {rep.motivos.filter(m => m.detalhe).map((m, i) => (
                          <li key={i}><span className="font-medium">{m.nome}:</span> {m.detalhe}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* 📎 Reenvio de anexo pedido pela equipe interna.
              Aparece antes de tudo porque é a única coisa que trava o andamento. */}
          {submissaoSelecionada.solicitacaoReenvio?.status === 'pendente' && (() => {
            const solicitacao = submissaoSelecionada.solicitacaoReenvio!;
            return (
              <div className="mb-4 rounded-2xl overflow-hidden border-2 border-red-300 bg-white shadow-lg">
                <div className="bg-red-50 border-b border-red-200 px-5 py-4">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl leading-none">📎</span>
                    <div>
                      <h3 className="font-semibold text-red-800">Reenvie o arquivo solicitado</h3>
                      <p className="text-sm text-red-700 mt-0.5">
                        A análise foi interrompida. Envie novamente {solicitacao.campos.length === 1 ? 'o arquivo abaixo' : 'os arquivos abaixo'} para
                        que sua solicitação volte a andar.
                      </p>
                      {solicitacao.observacao && (
                        <p className="text-sm text-red-700 mt-2 whitespace-pre-line bg-white/70 border border-red-200 rounded-lg p-2">
                          {solicitacao.observacao}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-5 space-y-4">
                  {solicitacao.campos.map(campo => {
                    const maxAnexos = Number(campo.maxAnexos || 0) || 6;
                    const minAnexos = Number(campo.minAnexos || 0) || 1;
                    const arquivos = arquivosReenvio[campo.campoId] || [];
                    const prontos = arquivos.filter((a: any) => a.status === 'uploaded').length;
                    const atingiuMax = arquivos.length >= maxAnexos;

                    return (
                      <div key={campo.campoId} className="border border-gray-200 rounded-xl p-4">
                        <label className="block font-medium text-gray-900 mb-1">
                          {campo.nome} <span className="text-red-500">*</span>
                        </label>
                        {campo.motivo && (
                          <p className="text-sm text-red-600 mb-2">Motivo: {campo.motivo}</p>
                        )}
                        <div className={`text-[11px] px-2 py-1 rounded inline-block mb-2 ${
                          prontos < minAnexos ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'
                        }`}>
                          📎 {prontos}/{maxAnexos} anexos{minAnexos > 1 ? ` • mínimo ${minAnexos}` : ''}
                        </div>

                        {arquivos.length > 0 && (
                          <div className="space-y-2 mb-2">
                            {arquivos.map((arq: any, idx: number) => (
                              <div key={arq.localId || idx} className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg p-2">
                                <span className="text-lg">
                                  {arq.status === 'uploading' ? '⏳' : arq.status === 'error' ? '⚠️' : '✅'}
                                </span>
                                <div className="flex-1 min-w-0 text-left">
                                  <p className="text-sm font-medium text-gray-800 truncate">{arq.name}</p>
                                  <p className="text-xs text-gray-500">
                                    {arq.size ? `${(arq.size / 1024).toFixed(1)} KB` : ''}
                                    {arq.status === 'uploading' && ' · enviando...'}
                                    {arq.status === 'uploaded' && ' · pronto'}
                                    {arq.status === 'error' && ` · falhou${arq.erro ? `: ${arq.erro}` : ''}`}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setArquivosReenvio(prev => ({
                                    ...prev,
                                    [campo.campoId]: (prev[campo.campoId] || []).filter((_: any, i: number) => i !== idx),
                                  }))}
                                  className="p-1 text-red-500 hover:bg-red-50 rounded flex-shrink-0"
                                  title="Remover"
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className={`border-2 border-dashed rounded-lg p-4 text-center relative transition-colors ${
                          atingiuMax ? 'border-gray-200 bg-gray-50' : 'border-gray-300 hover:border-gray-400 cursor-pointer'
                        }`}>
                          <p className="text-sm text-gray-600">
                            {atingiuMax
                              ? `Limite de ${maxAnexos} arquivos atingido`
                              : arquivos.length > 0 ? 'Adicionar mais arquivos' : 'Clique para escolher o arquivo'}
                          </p>
                          <input
                            type="file"
                            multiple
                            disabled={atingiuMax || enviandoReenvio}
                            onChange={async (e) => {
                              const novos = Array.from(e.target.files || []);
                              e.target.value = '';
                              if (novos.length === 0) return;
                              const espaco = maxAnexos - arquivos.length;
                              if (espaco <= 0) return;
                              const aceitos = novos.slice(0, espaco);
                              const comprimidos = await compressImagesIfNeeded(aceitos);
                              comprimidos.forEach(file => { uploadArquivoReenvio(campo.campoId, file); });
                            }}
                            className={`absolute inset-0 w-full h-full opacity-0 ${atingiuMax ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                          />
                        </div>
                      </div>
                    );
                  })}

                  {erroReenvio && (
                    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      {erroReenvio}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={enviarReenvio}
                    disabled={enviandoReenvio}
                    className="w-full py-3 rounded-xl text-white font-medium disabled:opacity-60 disabled:cursor-not-allowed transition-opacity"
                    style={{ background: `linear-gradient(135deg, ${getCorPrimaria()}, ${getCorSecundaria()})` }}
                  >
                    {enviandoReenvio ? 'Reenviando...' : 'Reenviar arquivos'}
                  </button>
                </div>
              </div>
            );
          })()}

          {/* ✅ Aprovação da entrega pelo solicitante */}
          {submissaoSelecionada.aprovacaoSolicitante && (() => {
            const ap = submissaoSelecionada.aprovacaoSolicitante!;
            const ehImagem = (a: { nome: string; type?: string }) =>
              (a.type || '').startsWith('image/') || /\.(png|jpe?g|gif|webp)$/i.test(a.nome || '');
            return (
              <div className="mb-4 rounded-2xl overflow-hidden border-2 border-indigo-300 bg-white shadow-lg">
                <div className="bg-indigo-50 border-b border-indigo-200 px-5 py-4">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl leading-none">📝</span>
                    <div>
                      <h3 className="font-semibold text-indigo-900">Sua aprovação é necessária</h3>
                      <p className="text-sm text-indigo-800 mt-0.5">{ap.pergunta}</p>
                    </div>
                  </div>
                </div>
                <div className="p-5 space-y-4">
                  {ap.entregas.map(ent => (
                    <div key={ent.campoId}>
                      <p className="text-sm font-medium text-gray-700 mb-2">{ent.nome}</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {ent.anexos.map((a, i) => (
                          <a key={i} href={`${API_BASE}${a.url}`} target="_blank" rel="noopener noreferrer"
                             className="block rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                            {ehImagem(a)
                              ? <img src={`${API_BASE}${a.url}`} alt={a.nome} className="w-full h-32 object-cover" />
                              : <div className="h-32 flex items-center justify-center text-3xl bg-gray-50">📄</div>}
                            <p className="text-xs text-gray-600 px-2 py-1 truncate">{a.nome}</p>
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                  {ap.entregas.length === 0 && (
                    <p className="text-sm text-gray-500">Nenhum arquivo foi anexado à entrega.</p>
                  )}
                  {ap.textos.map((t, i) => (
                    <div key={i} className="text-sm">
                      <span className="font-medium text-gray-700">{t.nome}: </span>
                      <span className="text-gray-600 whitespace-pre-line">{t.valor}</span>
                    </div>
                  ))}

                  <div className="flex gap-3">
                    {(['Sim', 'Não'] as const).map(op => (
                      <button key={op} type="button" disabled={enviandoAprovacao}
                        onClick={() => setDecisaoAprovacao(op)}
                        className={`flex-1 py-3 rounded-xl border-2 font-medium transition-colors ${
                          decisaoAprovacao === op
                            ? (op === 'Sim' ? 'border-green-500 bg-green-50 text-green-800' : 'border-red-500 bg-red-50 text-red-800')
                            : 'border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                        {op === 'Sim' ? '✅ Aprovo' : '❌ Não aprovo'}
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={comentarioAprovacao}
                    onChange={e => setComentarioAprovacao(e.target.value)}
                    disabled={enviandoAprovacao}
                    rows={3}
                    placeholder={decisaoAprovacao === 'Não' ? 'O que precisa mudar? (obrigatório)' : 'Comentários (opcional)'}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  />
                  {erroAprovacao && (
                    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{erroAprovacao}</div>
                  )}
                  <button type="button" onClick={enviarAprovacao} disabled={enviandoAprovacao}
                    className="w-full py-3 rounded-xl text-white font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ background: `linear-gradient(135deg, ${getCorPrimaria()}, ${getCorSecundaria()})` }}>
                    {enviandoAprovacao ? 'Salvando...' : 'Salvar resposta'}
                  </button>
                </div>
              </div>
            );
          })()}

          {aprovacaoConcluida && !submissaoSelecionada.aprovacaoSolicitante && (
            <div className="mb-4 rounded-xl border border-green-300 bg-green-50 px-4 py-3 flex items-start gap-3">
              <span className="text-xl leading-none">✅</span>
              <p className="flex-1 text-sm text-green-800">{aprovacaoConcluida}</p>
              <button type="button" onClick={() => setAprovacaoConcluida(null)} className="text-green-700 hover:text-green-900 text-sm">✕</button>
            </div>
          )}

          {/* Confirmação de reenvio */}
          {reenvioConcluido && submissaoSelecionada.solicitacaoReenvio?.status !== 'pendente' && (
            <div className="mb-4 rounded-xl border border-green-300 bg-green-50 px-4 py-3 flex items-start gap-3">
              <span className="text-xl leading-none">✅</span>
              <div className="flex-1">
                <p className="text-sm text-green-800">{reenvioConcluido}</p>
              </div>
              <button
                type="button"
                onClick={() => setReenvioConcluido(null)}
                className="text-green-700 hover:text-green-900 text-sm"
              >
                ✕
              </button>
            </div>
          )}

          {/* Card Principal */}
          <div className={`${getCardClass()} rounded-2xl overflow-hidden mb-4`}>
            {/* Header */}
            <div 
              className="p-5"
              style={{ background: `linear-gradient(135deg, ${getCorPrimaria()}, ${getCorSecundaria()})` }}
            >
              <div className="flex items-center justify-between text-white mb-2">
                <span className="font-mono font-bold text-lg">
                  {submissaoSelecionada.protocolo || 'Solicitação'}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusConfig.bg} ${statusConfig.text}`}>
                  {statusConfig.icon} {statusConfig.label}
                </span>
              </div>
              {/* 27/10 André: a etapa é o que a pessoa precisa ver (ex.: "Aprovados"); antes ficava miúda e passava batido */}
              {submissaoSelecionada.etapaAtual && (
                <div className="mt-3 inline-flex flex-col bg-white/95 rounded-xl px-4 py-2 shadow-sm">
                  <span className="text-[11px] uppercase tracking-wide font-semibold text-gray-500">Situação da sua solicitação</span>
                  <span className="text-2xl font-extrabold leading-tight" style={{ color: getCorPrimaria() }}>
                    {submissaoSelecionada.etapaAtual.nome}
                  </span>
                </div>
              )}
            </div>

            {/* Corpo */}
            <div className="p-4">
              {/* Resumo */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase font-medium">{submissaoSelecionada.etapaAtual ? 'Etapa atual' : 'Status'}</p>
                  <p className="font-bold text-base" style={{ color: getCorTexto() }}>
                    {submissaoSelecionada.etapaAtual?.nome || statusConfig.label}
                  </p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase font-medium">Enviado em</p>
                  <p className="font-medium text-sm" style={{ color: getCorTexto() }}>
                    {formatarData(submissaoSelecionada.createdAt)}
                  </p>
                </div>
              </div>

              {/* Dados Enviados */}
              <details className="mb-4" open>
                <summary 
                  className="cursor-pointer font-medium py-2 flex items-center gap-2"
                  style={{ color: getCorPrimaria() }}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Ver dados enviados
                </summary>
                <div className="mt-2 bg-gray-50 rounded-lg p-4 space-y-2">
                  {(portal as any)?.camposParaExibir?.length > 0 ? (
                    (portal as any).camposParaExibir.map((campo: any) => (
                      <div key={campo.id} className="flex flex-col sm:flex-row sm:justify-between py-2 border-b border-gray-200 last:border-0">
                        <span className="text-gray-600 font-medium text-sm">{campo.nome}</span>
                        <span className="font-medium text-right mt-1 sm:mt-0" style={{ color: getCorTexto() }}>
                          {Array.isArray(campo.valor) ? campo.valor.join(', ') : (campo.valor && typeof campo.valor === 'object') ? (campo.valor.name || campo.valor.nome || campo.valor.url || JSON.stringify(campo.valor)) : String(campo.valor || '-')}
                        </span>
                      </div>
                    ))
                  ) : submissaoSelecionada.dadosFormatados && Object.keys(submissaoSelecionada.dadosFormatados).length > 0 ? (
                    Object.entries(submissaoSelecionada.dadosFormatados).map(([nome, valor]) => (
                      <div key={nome} className="flex flex-col sm:flex-row sm:justify-between py-2 border-b border-gray-200 last:border-0">
                        <span className="text-gray-600 font-medium text-sm">{nome}</span>
                        <span className="font-medium text-right mt-1 sm:mt-0" style={{ color: getCorTexto() }}>
                          {Array.isArray(valor) ? (valor as any[]).join(', ') : (valor && typeof valor === 'object') ? ((valor as any).name || (valor as any).nome || (valor as any).url || JSON.stringify(valor)) : String(valor || '-')}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-400 text-center py-4">Nenhum dado disponível</p>
                  )}
                </div>
              </details>
            </div>
          </div>

          {/* Chat / Mensagens */}
          {portal?.funcionalidades?.permitirChat && (
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div 
                className="p-4 border-b border-gray-100 flex items-center gap-2"
                style={{ color: getCorTexto() }}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <h3 className="font-semibold">Mensagens</h3>
                {submissaoSelecionada.mensagens?.length > 0 && (
                  <span className="text-gray-400 text-sm">
                    ({submissaoSelecionada.mensagens.length})
                  </span>
                )}
                {novaResposta && (
                  <button
                    onClick={() => {
                      setNovaResposta(false);
                      if (mensagensRef.current) {
                        mensagensRef.current.scrollTop = mensagensRef.current.scrollHeight;
                      }
                    }}
                    className="ml-auto flex items-center gap-1 px-2 py-1 rounded-full bg-red-500 text-white text-xs font-bold animate-pulse"
                  >
                    💬 Nova resposta!
                  </button>
                )}
              </div>

              {/* Lista de Mensagens */}
              <div 
                ref={mensagensRef}
                className="h-64 overflow-y-auto p-4 space-y-3 bg-gray-50"
              >
                {!submissaoSelecionada.mensagens?.length ? (
                  <div className="text-center text-gray-400 py-8">
                    <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <p className="text-sm">Nenhuma mensagem ainda</p>
                    <p className="text-xs">Envie uma mensagem se precisar de ajuda</p>
                  </div>
                ) : (
                  submissaoSelecionada.mensagens.map(msg => (
                    <div 
                      key={msg.id}
                      className={`flex ${msg.origem === 'externo' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div 
                        className={`max-w-[85%] rounded-lg p-3 ${
                          msg.origem === 'externo' 
                            ? 'rounded-br-none text-white' 
                            : 'bg-white rounded-bl-none shadow-sm'
                        }`}
                        style={msg.origem === 'externo' ? { backgroundColor: getCorPrimaria() } : {}}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.mensagem}</p>
                        <p className={`text-xs mt-1 ${msg.origem === 'externo' ? 'text-white/70' : 'text-gray-400'}`}>
                          {msg.autorNome} • {formatarData(msg.createdAt)}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Input de Mensagem */}
              <div className="p-4 border-t border-gray-100">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={novaMensagem}
                    onChange={(e) => setNovaMensagem(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && enviarMensagem()}
                    placeholder="Digite sua mensagem..."
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none"
                    style={{ '--tw-ring-color': getCorPrimaria() } as any}
                  />
                  <button
                    onClick={enviarMensagem}
                    disabled={enviandoMensagem || !novaMensagem.trim()}
                    className="px-4 py-3 text-white rounded-lg transition-all disabled:opacity-50"
                    style={{ backgroundColor: getCorPrimaria() }}
                  >
                    {enviandoMensagem ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Renderização do Campo do Formulário
  const renderCampo = (campo: PortalCampoConfig) => {
    const nomeNormalizado = (campo.nome || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\*/g, '')
      .trim();
    const placeholderNormalizado = (campo.placeholder || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\*/g, '')
      .trim();

    if (
      nomeNormalizado === 'titulo' ||
      nomeNormalizado === 'titulo do cartao' ||
      placeholderNormalizado === 'digite o titulo do cartao' ||
      placeholderNormalizado.includes('titulo do cartao') ||
      campo.id === 'titulo' ||
      campo.campoOriginalId === 'titulo'
    ) {
      return null;
    }
    if (!campoVisivelPorAutomacao(campo)) return null;

    // Campos de tipo documento: renderizar inline com viewer
    if (campo.tipo === 'documento') {
      const docTemplateId = (campo as any).documentoTemplateId || campo.id.replace('doc_', '').replace(/^importado_doc_/, '').replace(/_\d+$/, '');
      const doc = portal?.documentos?.find(d => d.id === docTemplateId);
      if (!doc) return null;
      const isExpanded = documentoExpandido === doc.id;
      const isPdf = doc.tipo === 'pdf' && doc.pdfUrl;
      return (
        <div key={campo.id} className="mb-4">
          <div className="border border-gray-200 bg-white rounded-lg overflow-hidden">
            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-white">
              <div className="flex items-center gap-2 flex-1">
                <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div>
                  <h4 className="font-medium text-gray-900 text-sm">{doc.nome}</h4>
                  {doc.descricao && <p className="text-xs text-gray-500">{doc.descricao}</p>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setDocumentoExpandido(isExpanded ? null : doc.id)}
                  className="px-3 py-1.5 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors font-medium"
                >
                  {isExpanded ? 'Fechar' : 'Ver'}
                </button>
                {isPdf && (
                  <button
                    type="button"
                    onClick={() => window.open(doc.pdfUrl, '_blank')}
                    className="px-3 py-1.5 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors font-medium"
                  >
                    Ampliar
                  </button>
                )}
                {isPdf && (
                  <a
                    href={doc.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="px-3 py-1.5 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors font-medium"
                  >
                    Baixar
                  </a>
                )}
              </div>
            </div>
            {isExpanded && (
              <div className="bg-gray-50 border-t border-gray-200 max-h-[60vh] overflow-y-auto">
                {isPdf ? (
                  <iframe src={doc.pdfUrl} className="w-full h-[55vh] border-0" title={`PDF - ${doc.nome}`} />
                ) : doc.conteudoHtml ? (
                  <div className="p-3">
                    <div className="bg-white p-4 rounded shadow-sm">
                      <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: doc.conteudoHtml }} />
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      );
    }
    
    const valor = formData[campo.id];
    const erro = errosCampos[campo.id];
    
    const inputClasses = `w-full px-4 py-3 border rounded-lg focus:ring-2 focus:outline-none transition-colors ${
      erro ? 'border-red-300 focus:ring-red-200' : 'border-gray-300'
    }`;

    return (
      <div key={campo.id} className="mb-4">
        {!(campo.ocultarTitulo && campo.tipo === 'texto_informativo') && (
        <div className="flex items-center justify-between mb-1">
        <label className="block text-sm font-medium text-gray-700">
          {campo.nome}
          {campo.obrigatorio && <span className="text-red-500 ml-1">*</span>}
        </label>
        {['checkbox_single', 'lista', 'select', 'selecao', 'checkbox', 'checkbox_multi', 'progresso'].includes(campo.tipo) && valor && (Array.isArray(valor) ? valor.length > 0 : valor !== '') && (
          <button
            type="button"
            onClick={() => handleLimparCascata(campo.id)}
            className="text-[11px] text-red-500 bg-red-50 hover:text-white hover:bg-red-500 px-2 py-0.5 rounded-full transition-colors flex items-center gap-1 font-medium border border-red-200 hover:border-red-500"
            title="Limpar campo"
          >
            ✕ Limpar
          </button>
        )}
        </div>
        )}
        
        {campo.descricao && (
          <p className="text-xs text-gray-500 mb-2">{campo.descricao}</p>
        )}

        {/* Texto, Email, Telefone */}
        {['texto', 'email', 'telefone', 'cpf_cnpj'].includes(campo.tipo) && (
          <DebouncedInput
            type={campo.tipo === 'email' ? 'email' : 'text'}
            value={valor || ''}
            onChange={(val) => handleChange(campo.id, val)}
            placeholder={campo.placeholder || ''}
            className={inputClasses}
            style={{ '--tw-ring-color': getCorPrimaria() } as any}
          />
        )}

        {/* Textarea / Texto longo */}
        {(campo.tipo === 'textarea' || campo.tipo === 'texto_longo') && (
          <DebouncedInput
            value={valor || ''}
            onChange={(val) => handleChange(campo.id, val)}
            placeholder={campo.placeholder || ''}
            rows={4}
            className={inputClasses}
            style={{ '--tw-ring-color': getCorPrimaria() } as any}
          />
        )}

        {/* Número */}
        {campo.tipo === 'numero' && (
          <DebouncedInput
            type="text"
            inputMode="numeric"
            value={valor || ''}
            onChange={(val) => handleChange(campo.id, val.replace(/\D/g, ''))}
            placeholder={campo.placeholder || ''}
            className={inputClasses}
            style={{ '--tw-ring-color': getCorPrimaria() } as any}
          />
        )}

        {/* Data / Data de vencimento (com hora, igual ao formulário interno) */}
        {(campo.tipo === 'data' || campo.tipo === 'data_vencimento') && (
          <input
            type={campo.tipo === 'data_vencimento' ? 'datetime-local' : 'date'}
            value={valor || ''}
            onChange={(e) => handleChange(campo.id, e.target.value)}
            className={inputClasses}
            style={{ '--tw-ring-color': getCorPrimaria() } as any}
          />
        )}

        {/* Select/Lista/Seleção */}
        {(campo.tipo === 'lista' || campo.tipo === 'select' || campo.tipo === 'selecao') && (
          <select
            value={valor || ''}
            onChange={(e) => handleChange(campo.id, e.target.value)}
            className={inputClasses}
            style={{ '--tw-ring-color': getCorPrimaria() } as any}
          >
            <option value="">Selecione...</option>
            {getOpcoesVisiveis(campo.id, campo.opcoes?.map(o => typeof o === 'string' ? o : o.valor)).map((opcaoValor, i) => {
              const opcaoOriginal = campo.opcoes?.find(o => (typeof o === 'string' ? o : o.valor) === opcaoValor);
              const opcaoLabel = opcaoOriginal ? (typeof opcaoOriginal === 'string' ? opcaoOriginal : opcaoOriginal.label) : opcaoValor;
              return (
                <option key={i} value={opcaoValor}>
                  {opcaoLabel}
                </option>
              );
            })}
          </select>
        )}

        {/* Checkbox Single (Radio) */}
        {campo.tipo === 'checkbox_single' && campo.opcoes && (
          <div className="space-y-2">
            {getOpcoesVisiveis(campo.id, campo.opcoes.map(o => typeof o === 'string' ? o : o.valor)).map((opcaoValor, i) => {
              const opcaoOriginal = campo.opcoes?.find(o => (typeof o === 'string' ? o : o.valor) === opcaoValor);
              const opcaoLabel = opcaoOriginal ? (typeof opcaoOriginal === 'string' ? opcaoOriginal : opcaoOriginal.label) : opcaoValor;
              return (
                <label key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                  <input
                    type="radio"
                    name={campo.id}
                    value={opcaoValor}
                    checked={valor === opcaoValor}
                    onChange={(e) => handleChange(campo.id, e.target.value)}
                    className="w-4 h-4"
                    style={{ accentColor: getCorPrimaria() }}
                  />
                  <span className="text-sm">{opcaoLabel}</span>
                </label>
              );
            })}
          </div>
        )}

        {/* Checkbox Multi (inclui checkbox com opções) */}
        {(campo.tipo === 'checkbox_multi' || (campo.tipo === 'checkbox' && campo.opcoes?.length)) && campo.opcoes && (
          <div className="space-y-2">
            {(() => {
              const todasOpcoes = getOpcoesVisiveis(campo.id, campo.opcoes.map(o => typeof o === 'string' ? o : o.valor));
              const selecionados = Array.isArray(valor) ? valor : [];
              const allSelected = todasOpcoes.length > 0 && todasOpcoes.every((o: string) => selecionados.includes(o));
              const MAX_VISIVEIS = 5;
              const expandido = checkboxExpandido[campo.id] || false;
              const opcoesParaMostrar = todasOpcoes.length > MAX_VISIVEIS && !expandido ? todasOpcoes.slice(0, MAX_VISIVEIS) : todasOpcoes;
              return (
                <>
                  {todasOpcoes.length > 3 && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">{todasOpcoes.length} opções</span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleChange(campo.id, allSelected ? [] : [...todasOpcoes])}
                          className="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                          style={{ color: getCorPrimaria(), backgroundColor: `${getCorPrimaria()}15` }}
                        >
                          {allSelected ? 'Desmarcar todos' : 'Marcar todos'}
                        </button>
                        {todasOpcoes.length > MAX_VISIVEIS && (
                          <button
                            type="button"
                            onClick={() => setCheckboxExpandido(prev => ({ ...prev, [campo.id]: !prev[campo.id] }))}
                            className="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors text-gray-600 bg-gray-100 hover:bg-gray-200"
                          >
                            {expandido ? '∧ Recolher' : '∨ Expandir'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {opcoesParaMostrar.map((opcaoValor, i) => {
                    const opcaoOriginal = campo.opcoes?.find(o => (typeof o === 'string' ? o : o.valor) === opcaoValor);
                    const opcaoLabel = opcaoOriginal ? (typeof opcaoOriginal === 'string' ? opcaoOriginal : opcaoOriginal.label) : opcaoValor;
                    return (
                      <label key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                        <input
                          type="checkbox"
                          value={opcaoValor}
                          checked={selecionados.includes(opcaoValor)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              handleChange(campo.id, [...selecionados, opcaoValor]);
                            } else {
                              handleChange(campo.id, selecionados.filter((v: string) => v !== opcaoValor));
                            }
                          }}
                          className="w-4 h-4 rounded"
                          style={{ accentColor: getCorPrimaria() }}
                        />
                        <span className="text-sm">{opcaoLabel}</span>
                      </label>
                    );
                  })}
                  {todasOpcoes.length > MAX_VISIVEIS && !expandido && (
                    <button
                      type="button"
                      onClick={() => setCheckboxExpandido(prev => ({ ...prev, [campo.id]: true }))}
                      className="text-xs text-gray-500 hover:text-gray-700 py-1 transition-colors"
                    >
                      Mostrar mais...
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Checkbox Boolean */}
        {campo.tipo === 'checkbox' && !campo.opcoes?.length && (
          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
            <input
              type="checkbox"
              checked={valor === true}
              onChange={(e) => handleChange(campo.id, e.target.checked)}
              className="w-5 h-5 rounded"
              style={{ accentColor: getCorPrimaria() }}
            />
            <span className="text-sm">{campo.placeholder || 'Sim'}</span>
          </label>
        )}

        {/* Arquivo / Anexo */}
        {(campo.tipo === 'arquivo' || campo.tipo === 'anexo') && (() => {
          // Itens podem ser File (legado) ou objetos {__temp:true, status, name, size, type, tempId?}
          const arquivosCampo: any[] = Array.isArray(valor)
            ? (valor as any[])
            : valor ? [valor] : [];
          const portalNome = (portal?.nome || '').toLowerCase();
          const isChecklistLojas = portalNome.includes('cd') && portalNome.includes('loja');
          const minAnexos = Number(campo.minAnexos || 0);
          const maxAnexos = Number(campo.maxAnexos || 0) || 6;
          const enviadosOk = arquivosCampo.filter(a => !(a && typeof a === 'object' && a.__temp === true && a.status !== 'uploaded')).length;
          const atingiuMax = arquivosCampo.length >= maxAnexos;
          return (
            <div className="space-y-2">
              {/* Contador / exigência de quantidade */}
              <div className={`text-[11px] px-2 py-1 rounded inline-block ${
                minAnexos > 0 && enviadosOk < minAnexos
                  ? 'bg-red-50 text-red-600'
                  : atingiuMax ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-500'
              }`}>
                📎 {enviadosOk}/{maxAnexos} anexos
                {minAnexos > 0 && ` • mínimo ${minAnexos}`}
              </div>
              {/* Lista de arquivos já selecionados */}
              {arquivosCampo.length > 0 && (
                <div className="space-y-2">
                  {arquivosCampo.map((arq, idx) => {
                    const isTemp = arq && typeof arq === 'object' && arq.__temp === true;
                    const status: 'uploading' | 'uploaded' | 'error' = isTemp ? arq.status : 'uploaded';
                    const nome = arq?.name || arq?.originalName || 'Arquivo';
                    const tamanho = arq?.size ? `${(arq.size / 1024).toFixed(1)} KB` : '';
                    const corIcone = status === 'error' ? 'text-red-500' : status === 'uploading' ? 'text-gray-400' : 'text-green-500';
                    return (
                      <div key={`${nome}-${idx}`} className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg p-2">
                        {status === 'uploading' ? (
                          <svg className={`w-6 h-6 ${corIcone} flex-shrink-0 animate-spin`} fill="none" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
                            <path d="M22 12a10 10 0 00-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                          </svg>
                        ) : status === 'error' ? (
                          <svg className={`w-6 h-6 ${corIcone} flex-shrink-0`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                          </svg>
                        ) : (
                          <svg className={`w-6 h-6 ${corIcone} flex-shrink-0`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                        <div className="flex-1 min-w-0 text-left">
                          <p className="text-sm font-medium text-gray-800 truncate">{nome}</p>
                          <p className="text-xs text-gray-500">
                            {tamanho}
                            {status === 'uploading' && ' · enviando...'}
                            {status === 'uploaded' && ' · ✓ salvo no servidor'}
                            {status === 'error' && ` · falhou${arq.erro ? `: ${arq.erro}` : ''}`}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const novos = arquivosCampo.filter((_, i) => i !== idx);
                            handleChange(campo.id, novos.length === 0 ? null : novos);
                          }}
                          className="p-1 text-red-500 hover:bg-red-50 rounded flex-shrink-0"
                          title="Remover"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Botão de upload (sempre visível, permite adicionar mais) */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors cursor-pointer relative">
                <svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-sm text-gray-600">
                  {atingiuMax
                    ? `Limite de ${maxAnexos} arquivos atingido`
                    : arquivosCampo.length > 0 ? 'Adicionar mais arquivos' : 'Clique ou arraste para enviar'}
                </p>
                <input
                  id={`file-input-${campo.id}`}
                  type="file"
                  multiple
                  disabled={atingiuMax}
                  onChange={async (e) => {
                    const novos = Array.from(e.target.files || []);
                    e.target.value = '';
                    if (novos.length === 0) return;
                    const espaco = maxAnexos - arquivosCampo.length;
                    if (espaco <= 0) {
                      alert(`Limite de ${maxAnexos} arquivos neste campo. Remova um para adicionar outro.`);
                      return;
                    }
                    const aceitos = novos.slice(0, espaco);
                    if (aceitos.length < novos.length) {
                      alert(`Você pode adicionar no máximo ${espaco} arquivo(s) neste campo. Os demais foram ignorados.`);
                    }
                    const comprimidos = await compressImagesIfNeeded(aceitos);
                    // Disparar upload imediato de cada arquivo (em paralelo)
                    comprimidos.forEach(file => { uploadArquivoTemp(campo.id, file); });
                  }}
                  className={`absolute inset-0 w-full h-full opacity-0 ${atingiuMax ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  accept={campo.tiposAceitos || '*'}
                />
              </div>

              {/* Campo de observação (só no portal de checklist de Lojas e após ter ao menos 1 arquivo) */}
              {isChecklistLojas && arquivosCampo.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Observação {campo.nome ? `de ${String(campo.nome).toLowerCase()}` : ''}
                  </label>
                  <textarea
                    value={observacoesArquivos[campo.id] || ''}
                    onChange={(e) => setObservacoesArquivos(prev => ({ ...prev, [campo.id]: e.target.value }))}
                    placeholder={`Descreva algo sobre ${String(campo.nome || 'os arquivos').toLowerCase()} (opcional)`}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30 focus:border-gray-400 resize-none"
                  />
                </div>
              )}
            </div>
          );
        })()}

        {/* Texto Informativo (Label) */}
        {campo.tipo === 'texto_informativo' && (
          <div className={`px-3 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg 
            ${campo.formatacao?.alinhamento === 'centro' ? 'text-center' : 
              campo.formatacao?.alinhamento === 'direita' ? 'text-right' : 'text-left'}`}>
            <span style={{ 
              fontWeight: campo.formatacao?.negrito ? 'bold' : 'normal', 
              fontStyle: campo.formatacao?.italico ? 'italic' : 'normal', 
              fontSize: campo.formatacao?.tamanho === 'pequeno' ? '11px' : 
                        campo.formatacao?.tamanho === 'grande' ? '16px' : '13px', 
              color: campo.formatacao?.cor || '#374151' 
            }}>
              {campo.textoInformativo || ''}
            </span>
          </div>
        )}

        {/* Roteiro aéreo — trechos de ida/volta, igual ao formulário interno */}
        {campo.tipo === 'rotas' && (
          <RotasField
            rotasAtuais={Array.isArray(valor) ? valor : []}
            campoId={campo.id}
            updateFieldValue={(campoId: string, novoValor: any) => handleChange(campoId, novoValor)}
          />
        )}

        {/* Rota terrestre — trechos de carro */}
        {campo.tipo === 'rota_terrestre' && (
          <RotaTerrestreField
            rotasAtuais={Array.isArray(valor) ? valor : []}
            campoId={campo.id}
            updateFieldValue={(campoId: string, novoValor: any) => handleChange(campoId, novoValor)}
            consumoMedioPadrao={(campo as any).consumoMedioPadrao}
            precoCombustivelPadrao={(campo as any).precoCombustivelPadrao}
          />
        )}

        {/* Moeda / Valor */}
        {(campo.tipo === 'moeda' || campo.tipo === 'valor') && (
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
            <input
              type="text"
              inputMode="numeric"
              value={(() => {
                const raw = String(valor || '');
                if (!raw) return '';
                // Se já está formatado (tem vírgula), mostrar como está
                if (raw.includes(',')) return raw;
                // Se é número com ponto decimal (ex: 123.89), converter para formato BR
                if (raw.includes('.')) {
                  const num = parseFloat(raw);
                  if (!isNaN(num)) {
                    const parts = num.toFixed(2).split('.');
                    const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                    return `${intPart},${parts[1]}`;
                  }
                }
                // Se é só dígitos, aplicar máscara (últimos 2 = centavos)
                let digits = raw.replace(/\D/g, '');
                if (!digits || digits === '0' || digits === '00') return '';
                digits = digits.replace(/^0+/, '') || '0';
                digits = digits.padStart(3, '0');
                if (digits.length > 12) digits = digits.slice(0, 12);
                const dec = digits.slice(-2);
                let intPart = digits.slice(0, -2);
                intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                return `${intPart},${dec}`;
              })()}
              onChange={(e) => {
                let digits = e.target.value.replace(/\D/g, '');
                if (!digits) { handleChange(campo.id, ''); return; }
                digits = digits.replace(/^0+/, '') || '0';
                if (digits.length > 12) digits = digits.slice(0, 12);
                // Salvar formatado: últimos 2 dígitos = centavos
                digits = digits.padStart(3, '0');
                const dec = digits.slice(-2);
                let intPart = digits.slice(0, -2);
                intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                handleChange(campo.id, `${intPart},${dec}`);
              }}
              placeholder="0,00"
              className={`${inputClasses} pl-12`}
              style={{ '--tw-ring-color': getCorPrimaria() } as any}
            />
          </div>
        )}

        {erro && (
          <p className="text-sm text-red-600 mt-1">{erro}</p>
        )}
      </div>
    );
  };

  // Tela do Formulário
  const TelaFormulario = () => (
    <div className="flex-1 py-6 px-4">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => usuarioEmail ? setTelaAtual('dashboard') : setTelaAtual('inicio')}
          className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-900 mb-6 transition-colors group"
        >
          <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm font-medium">Voltar</span>
        </button>

        <div className={`${getCardClass()} rounded-2xl overflow-hidden`}>
          {/* Accent top bar */}
          <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${getCorPrimaria()}, ${getCorSecundaria()})` }} />
          
          <div className="p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-1" style={{ color: getCorTexto() }}>
              {portal.nome}
            </h2>
            {portal.descricao && (
              <p className="text-gray-500 mb-8 text-sm">{portal.descricao}</p>
            )}

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-700 rounded-xl text-sm flex items-center gap-3">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Campo de E-mail obrigatório */}
              <div className="mb-8 p-5 rounded-xl border" style={{ backgroundColor: getCorPrimaria() + '08', borderColor: getCorPrimaria() + '20' }}>
                <label className="block text-sm font-semibold mb-2" style={{ color: getCorPrimaria() }}>
                  <span className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Seu E-mail para Atualizações *
                  </span>
                </label>
                <DebouncedInput
                  type="email"
                  value={emailContato}
                  onChange={(val) => {
                    setEmailContato(val);
                    if (erroEmailContato) setErroEmailContato('');
                  }}
                  placeholder="seu@email.com"
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:outline-none transition-all ${
                    erroEmailContato ? 'border-red-400 bg-red-50 focus:ring-red-200' : 'border-gray-200 focus:ring-2 bg-white'
                  }`}
                  style={!erroEmailContato ? { '--tw-ring-color': getCorPrimaria() + '40', borderColor: 'rgb(229 231 235)' } as any : {}}
                />
                {erroEmailContato && (
                  <p className="text-red-600 text-sm mt-1.5">{erroEmailContato}</p>
                )}
                <p className="text-xs mt-2 opacity-70" style={{ color: getCorPrimaria() }}>
                  Você receberá o protocolo e atualizações neste e-mail.
                </p>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3 mb-6">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Dados da Solicitação</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              {(() => {
                return portal.campos
                  ?.filter(c => {
                    const nomeNormalizado = (c.nome || '')
                      .toLowerCase()
                      .normalize('NFD')
                      .replace(/[\u0300-\u036f]/g, '')
                      .replace(/\*/g, '')
                      .trim();

                    return nomeNormalizado !== 'titulo' && c.campoOriginalId !== 'titulo';
                  })
                  .map(campo => renderCampo(campo));
              })()}

              <button
                type="submit"
                disabled={enviando}
                className="w-full mt-8 py-4 text-white font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2"
                style={{ background: `linear-gradient(135deg, ${getCorPrimaria()}, ${getCorSecundaria()})` }}
              >
                {enviando ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white"></div>
                    Enviando...
                  </span>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    {portal.textos?.botaoEnviar || 'Enviar Solicitação'}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
        
        {/* Footer */}
        <div className="mt-6 py-4 text-center">
          <span className="text-xs text-gray-400">Powered by <strong className="font-medium text-gray-500">Escalone</strong></span>
        </div>
      </div>
    </div>
  );

  // Tela de Sucesso
  const TelaSucesso = () => (
    <div className="flex-1 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {/* Animated checkmark */}
        <div className="relative w-24 h-24 mx-auto mb-8">
          <div className="absolute inset-0 rounded-full animate-ping opacity-20" style={{ backgroundColor: getCorPrimaria() }} />
          <div 
            className="relative w-24 h-24 rounded-full flex items-center justify-center shadow-lg"
            style={{ background: `linear-gradient(135deg, ${getCorPrimaria()}, ${getCorSecundaria()})` }}
          >
            <svg className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        
        <h2 className="text-2xl sm:text-3xl font-extrabold mb-3" style={{ color: getCorTexto() }}>
          Solicitação Enviada!
        </h2>
        
        <p className="text-gray-500 mb-6">
          {portal.textos?.mensagemSucesso || 'Seu formulário foi enviado com sucesso!'}
        </p>

        {submissaoResposta?.protocolo && (
          <div className={`${getCardClass()} rounded-2xl p-6 mb-6`}>
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Número do Protocolo</p>
            <p 
              className="text-3xl font-mono font-bold tracking-wider"
              style={{ color: getCorPrimaria() }}
            >
              {submissaoResposta.protocolo}
            </p>
          </div>
        )}

        {/* E-mail notice */}
        <div className="rounded-xl p-5 mb-8 border" style={{ backgroundColor: getCorPrimaria() + '08', borderColor: getCorPrimaria() + '20' }}>
          <div className="flex items-center justify-center gap-2 mb-2" style={{ color: getCorPrimaria() }}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="font-semibold text-sm">Acompanhe por E-mail</span>
          </div>
          <p className="text-sm" style={{ color: getCorPrimaria() + 'cc' }}>
            Enviamos uma confirmação para <strong>{emailConfirmacao}</strong>.
            <br />
            Você receberá todas as atualizações neste e-mail.
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => {
              setSubmissaoResposta(null);
              setEmailContato('');
              inicializarFormulario(portal);
              setTelaAtual('formulario');
            }}
            className="w-full py-4 bg-white/80 backdrop-blur-sm border-2 font-semibold rounded-xl transition-all duration-300 hover:shadow-md hover:-translate-y-0.5"
            style={{ borderColor: getCorPrimaria() + '40', color: getCorPrimaria() }}
          >
            + Nova Solicitação
          </button>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // RENDER PRINCIPAL
  // ============================================================

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={getFundoStyle()}
    >
      {Header()}
      
      {telaAtual === 'inicio' && TelaInicio()}
      {telaAtual === 'login' && TelaLogin()}
      {telaAtual === 'dashboard' && TelaDashboard()}
      {telaAtual === 'formulario' && TelaFormulario()}
      {telaAtual === 'detalhes' && TelaDetalhes()}
      {telaAtual === 'sucesso' && TelaSucesso()}
    </div>
  );
};

export default PortalPublico;
